import { buildReceiptHtml, buildOrderReceiptHtml, buildShiftReportHtml, buildCustomerQRReceiptHtml, buildLabelHtml, buildReturnReceiptHtml, getReceiptConfig, getReceiptPrintCss, getReceiptAdjustmentLabels, getReceiptPaymentMethodLabel, formatSimplifiedTxNum, formatOrderDateTime, detectVehicleUnit, getPhysicalProductUnit } from "./receiptConfig";
import { normalizePublicUrl } from "./urlUtils";
import EscPosEncoder from 'esc-pos-encoder';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import html2canvas from 'html2canvas';
import { sendRawUsbBytes, getSavedUsbPrinter } from "./usbPrintManager";
import { sendRawSerialBytes, getSavedSerialPrinter } from "./serialPrintManager";
import { sendRawWindowsBytes, getSavedWindowsPrinter, setSavedWindowsPrinter, isWindowsPrintBridgeAvailable, listWindowsPrinters } from "./windowsPrintBridge";
import { apiClient } from "../api/client";
import { calculateOrderItemTotals } from "./salesUtils";

export function groupItems(items) {
  const grouped = [];
  
  (items || []).forEach(item => {
    // If the item itself already has selected_options (e.g. from POS cart)
    if (item.selected_options && item.selected_options.length > 0) {
      grouped.push({
        ...item,
        name: item.name || item.item_name || item.product_name || "",
        variant_name: item.variant_name || "",
        addons: item.selected_options.map(opt => ({
          ...opt,
          name: (opt.name || opt.item_name || "Ekstra").replace(/^\+\s*/, ''),
          qty: Number(opt.qty || opt.quantity || 1),
          price: Number(opt.price || opt.sell_price || opt.unit_price || 0),
          total: Number(opt.total ?? opt.total_price ?? ((opt.qty || opt.quantity || 1) * (opt.price || opt.sell_price || opt.unit_price || 0)))
        }))
      });
      return;
    }

    const rawName = item.item_name || item.name || item.product_name || "";
    const isAddon =
      item.item_type === 'ADDON' ||
      Boolean(item.product_addon_id) ||
      Boolean(item.addon_id) ||
      Boolean(item.product_addon) ||
      rawName.startsWith('+ ');

    if (!isAddon) {
      grouped.push({
        ...item,
        name: rawName,
        variant_name: item.variant_name || "",
        addons: []
      });
    } else {
      // Find parent item in grouped that matches the product_id or the last parent product
      let parent = null;
      for (let i = grouped.length - 1; i >= 0; i--) {
        const parentId = grouped[i].product_id || grouped[i].id;
        const addonParentId = item.product_id;
        if ((parentId && addonParentId && parentId === addonParentId) || (grouped[i].item_type || "PRODUCT") === "PRODUCT") {
          parent = grouped[i];
          break;
        }
      }
      
      let cleanName = rawName.replace(/^\+\s*/, '');
      if (parent) {
        const parentName = parent.name || "";
        if (parentName && cleanName.toLowerCase().startsWith(parentName.toLowerCase() + " - ")) {
          cleanName = cleanName.substring(parentName.length + 3);
        } else {
          const parts = cleanName.split(" - ");
          if (parts.length > 1 && parentName && parts[0].toLowerCase().includes(parentName.toLowerCase())) {
            cleanName = parts[parts.length - 1];
          }
        }
        
        parent.addons.push({
          ...item,
          name: cleanName,
          qty: Number(item.quantity || item.qty || 1),
          price: Number(item.unit_price || item.price || 0),
          total: Number(item.total_price ?? item.total ?? ((item.quantity || item.qty || 1) * (item.unit_price || item.price || 0)))
        });
      } else {
        grouped.push({
          ...item,
          name: cleanName,
          variant_name: item.variant_name || "",
          addons: []
        });
      }
    }
  });

  return grouped;
}

// Fungsi meminta izin bluetooth (perangkat sekitar) di Android 12+
const requestBluetoothPermissions = (onSuccess) => {
  if (window.cordova && window.cordova.plugins && window.cordova.plugins.permissions) {
    const permissions = window.cordova.plugins.permissions;
    const list = [
      "android.permission.BLUETOOTH_CONNECT",
      "android.permission.BLUETOOTH_SCAN"
    ];
    permissions.requestPermissions(
      list,
      () => onSuccess(),
      () => onSuccess() // Tetap lanjut ke onSuccess sebagai fallback (misal Android 11 ke bawah)
    );
  } else {
    onSuccess();
  }
};

// Manajemen state koneksi aktif printer Bluetooth agar switching antar printer (Utama, Split Order, Label) tidak salah alamat
let activeBluetoothMac = null;

export const getActiveBluetoothMac = () => activeBluetoothMac;

export const setActiveBluetoothMac = (mac) => {
  activeBluetoothMac = mac ? String(mac).trim().toUpperCase() : null;
};

export const resetBluetoothConnectionState = () => {
  activeBluetoothMac = null;
};

export const sendBluetoothRawBytes = (macAddress, rawBuffer) => {
  return new Promise((resolve, reject) => {
    if (!window.bluetoothSerial) {
      return reject(new Error("Printer Bluetooth hanya tersedia di aplikasi Android."));
    }
    const targetMac = String(macAddress || "").trim().toUpperCase();
    if (!targetMac) {
      return reject(new Error("Alamat MAC printer Bluetooth belum disetel."));
    }

    requestBluetoothPermissions(() => {
      const doWrite = () => {
        window.bluetoothSerial.write(
          rawBuffer,
          () => resolve({ method: 'bluetooth', success: true }),
          (err) => {
            activeBluetoothMac = null;
            reject(new Error("Gagal mengirim data ke printer Bluetooth: " + (err?.message || err)));
          }
        );
      };

      const connectWithRetry = (retryCount = 1) => {
        window.bluetoothSerial.connect(
          targetMac,
          () => {
            activeBluetoothMac = targetMac;
            doWrite();
          },
          (err) => {
            if (retryCount > 0) {
              console.warn(`Koneksi ke ${targetMac} gagal, mencoba ulang dalam 500ms...`, err);
              setTimeout(() => {
                window.bluetoothSerial.disconnect(
                  () => {
                    setTimeout(() => connectWithRetry(retryCount - 1), 500);
                  },
                  () => {
                    setTimeout(() => connectWithRetry(retryCount - 1), 500);
                  }
                );
              }, 500);
            } else {
              activeBluetoothMac = null;
              reject(new Error(`Gagal menghubungkan ke printer Bluetooth (${targetMac}): ${err?.message || err}`));
            }
          }
        );
      };

      window.bluetoothSerial.isConnected(
        () => {
          // Sedang ada koneksi aktif bluetooth
          if (activeBluetoothMac && activeBluetoothMac.toLowerCase() === targetMac.toLowerCase()) {
            // Sudah terhubung ke printer target yang BENAR
            doWrite();
          } else {
            // Sedang terhubung ke printer LAIN (misal sebelumnya cetak label atau printer beda)
            // WAJIB disconnect dulu, beri jeda aman 500ms agar socket radio dilepas Android, lalu connect ke targetMac!
            window.bluetoothSerial.disconnect(
              () => {
                activeBluetoothMac = null;
                setTimeout(() => connectWithRetry(1), 500);
              },
              () => {
                activeBluetoothMac = null;
                setTimeout(() => connectWithRetry(1), 500);
              }
            );
          }
        },
        () => {
          // Belum terhubung ke printer mana pun
          activeBluetoothMac = null;
          connectWithRetry(1);
        }
      );
    });
  });
};

const executeSinglePrint = async (receiptData, type, resolve, reject, options = {}) => {
  const isKuliner = localStorage.getItem("PREVIEW_APPS") === "true";
  const config = getReceiptConfig();
  const isThermal = config.orientation === 'thermal';

  // 1. Bluetooth Android (Prioritas di aplikasi Android)
  if (window.bluetoothSerial && isThermal) {
    const savedBt = localStorage.getItem('savedBluetoothPrinter')
      ? JSON.parse(localStorage.getItem('savedBluetoothPrinter'))
      : null;
    const targetMac = options.printerMac || savedBt?.address;

    if (targetMac) {
      try {
        const buffer = await generateRawEscPosReceipt(receiptData, type, false, options);
        await sendBluetoothRawBytes(targetMac, buffer);
        resolve({ method: 'bluetooth', success: true });
        return;
      } catch (err) {
        console.error("Koneksi Bluetooth gagal:", err);
        if (isKuliner) {
          reject(new Error(err?.message || "Printer belum terhubung. Silakan periksa Pengaturan Printer."));
        } else {
          doWebPrintFallback(receiptData, type, resolve, reject, options);
        }
        return;
      }
    } else {
      if (isKuliner) {
        reject(new Error("Printer belum terhubung. Silakan sambungkan printer di Pengaturan Printer."));
      } else {
        doWebPrintFallback(receiptData, type, resolve, reject, options);
      }
      return;
    }
  }

  // 2. Direct USB OTG
  const savedUsb = getSavedUsbPrinter();
  if (isThermal && savedUsb) {
    try {
      const buffer = await generateRawEscPosReceipt(receiptData, type, false, { ...options, cutFeedLines: 4 });
      await sendRawUsbBytes(buffer);
      resolve({ method: 'usb', success: true });
      return;
    } catch (usbErr) {
      console.error("Koneksi USB gagal:", usbErr);
      if (isKuliner) {
        reject(new Error(usbErr?.message || "Printer belum terhubung. Silakan periksa Pengaturan Printer."));
        return;
      }
      doWebPrintFallback(receiptData, type, resolve, reject, options);
      return;
    }
  }

  // 3. COM Bluetooth atau virtual COM Windows
  const savedSerial = getSavedSerialPrinter();
  if (isThermal && savedSerial) {
    try {
      const buffer = await generateRawEscPosReceipt(receiptData, type, false, options);
      await sendRawSerialBytes(buffer);
      resolve({ method: 'serial', success: true });
      return;
    } catch (serialErr) {
      console.error("Koneksi serial gagal:", serialErr);
      if (isKuliner) {
        reject(new Error(serialErr?.message || "Printer belum terhubung. Silakan periksa Pengaturan Printer."));
        return;
      }
      doWebPrintFallback(receiptData, type, resolve, reject, options);
      return;
    }
  }

  // 4. Printer Windows Spooler
  const savedWindowsPrinter = getSavedWindowsPrinter();
  let targetWindowsPrinterName = options.printerName || savedWindowsPrinter?.name;
  if (!targetWindowsPrinterName) {
    try {
      const isBridgeUp = await isWindowsPrintBridgeAvailable();
      if (isBridgeUp) {
        const printers = await listWindowsPrinters();
        if (printers && printers.length > 0) {
          targetWindowsPrinterName = printers[0];
          setSavedWindowsPrinter(printers[0]);
        }
      }
    } catch (_) {}
  }
  if (targetWindowsPrinterName) {
    try {
      const buffer = await generateRawEscPosReceipt(receiptData, type, false, { ...options, cutFeedLines: isThermal ? 4 : 2 });
      await sendRawWindowsBytes(buffer, targetWindowsPrinterName);
      resolve({ method: 'windows-spooler', success: true });
      return;
    } catch (bridgeErr) {
      console.error("Printer bridge gagal:", bridgeErr);
      if (isKuliner) {
        reject(new Error(bridgeErr?.message || "Printer belum terhubung. Silakan periksa Pengaturan Printer."));
        return;
      }
      doWebPrintFallback(receiptData, type, resolve, reject, options);
      return;
    }
  }

  if (isKuliner) {
    reject(new Error("Printer belum terhubung. Silakan sambungkan printer di Pengaturan Printer."));
  } else {
    doWebPrintFallback(receiptData, type, resolve, reject, options);
  }
};

/**
 * Mengirim pulsa RJ11 ke printer kasir aktif untuk membuka laci kasir (Cash Drawer).
 * Mendukung printer Bluetooth Android, USB OTG, COM serial, maupun Windows Spooler helper.
 */
export const executeOpenCashDrawer = async () => {
  const config = getReceiptConfig();
  const isThermal = config.orientation === 'thermal';
  const pulseBytes = new Uint8Array([
    0x1B, 0x70, 0x00, 0x19, 0xFA, // Pin 2
    0x1B, 0x70, 0x01, 0x19, 0xFA  // Pin 5
  ]);

  // 1. Android Bluetooth
  if (window.bluetoothSerial && isThermal) {
    const savedBt = localStorage.getItem('savedBluetoothPrinter')
      ? JSON.parse(localStorage.getItem('savedBluetoothPrinter'))
      : null;
    const targetMac = savedBt?.address;
    if (targetMac) {
      await sendBluetoothRawBytes(targetMac, pulseBytes);
      return { method: 'bluetooth', success: true };
    }
  }

  // 2. Direct USB OTG
  const savedUsb = getSavedUsbPrinter();
  if (isThermal && savedUsb) {
    await sendRawUsbBytes(pulseBytes);
    return { method: 'usb', success: true };
  }

  // 3. Serial / COM
  const savedSerial = getSavedSerialPrinter();
  if (isThermal && savedSerial) {
    await sendRawSerialBytes(pulseBytes);
    return { method: 'serial', success: true };
  }

  // 4. Windows Spooler Helper
  const savedWindows = getSavedWindowsPrinter();
  let targetWin = savedWindows?.name;
  if (!targetWin) {
    try {
      const isBridgeUp = await isWindowsPrintBridgeAvailable();
      if (isBridgeUp) {
        const printers = await listWindowsPrinters();
        if (printers && printers.length > 0) {
          targetWin = printers[0];
        }
      }
    } catch (_) {}
  }
  if (targetWin) {
    await sendRawWindowsBytes(pulseBytes, targetWin);
    return { method: 'windows-spooler', success: true };
  }

  throw new Error("Tidak ada printer kasir yang terhubung. Pastikan printer Bluetooth, USB, COM, atau Windows Spooler sudah tersambung.");
};

export const executePrint = async (receiptData, type = 'transaction') => {
  const isKuliner = localStorage.getItem("PREVIEW_APPS") === "true";
  const config = getReceiptConfig();
  
  if (type === 'order' && config.splitOrderEnabled && config.splitOrderReceipts && config.splitOrderReceipts.length > 0) {
    let combinedHtml = '';
    const hasSavedUsbPrinter = Boolean(getSavedUsbPrinter());
    const hasSavedSerialPrinter = Boolean(getSavedSerialPrinter());
    const hasSavedWindowsPrinter = Boolean(getSavedWindowsPrinter());
    const hasHardwarePrinter = Boolean(window.bluetoothSerial || hasSavedUsbPrinter || hasSavedSerialPrinter || hasSavedWindowsPrinter);

    if (isKuliner && !hasHardwarePrinter) {
      throw new Error("Printer belum terhubung. Silakan sambungkan printer di Pengaturan Printer.");
    }

    let useWebFallback = !isKuliner && (!hasHardwarePrinter);
    const failedMappings = [];
    
    for (const mapping of config.splitOrderReceipts) {
      const isAllCategories = mapping.categories === 'ALL' || (mapping.id === 'checker' && mapping.categories === undefined);
      const hasCategories = isAllCategories || (Array.isArray(mapping.categories) && mapping.categories.length > 0);
      
      // Jika nota pesanan (termasuk nota checker) tidak memiliki kategori yang dipilih, jangan dicetak
      if (!hasCategories) continue;

      const isChecker = isAllCategories;
      const mappedCats = Array.isArray(mapping.categories) ? mapping.categories : [];
      const mappedCatNames = Array.isArray(mapping.categoryNames) ? mapping.categoryNames : [];
      
      const filteredItems = isChecker 
        ? receiptData.items 
        : (receiptData.items || []).filter(item => {
            const itemCatId = String(item.category_id || item.product_category_id || "");
            const itemCatName = String(item.category_name || item.category || "").trim().toLowerCase();
            return mappedCats.some(c => {
              const matchStr = String(c || "").trim().toLowerCase();
              return (itemCatId && itemCatId.toLowerCase() === matchStr) || 
                     (itemCatName && itemCatName === matchStr);
            }) || mappedCatNames.some(cname => {
              const matchStr = String(cname || "").trim().toLowerCase();
              return itemCatName && itemCatName === matchStr;
            });
          });
        
      if (!filteredItems || filteredItems.length === 0) continue;
      
      const splitData = { ...receiptData, items: filteredItems };
      const options = { 
        customTitle: mapping.name, 
        printerMac: mapping.printerMac, 
        bodyOnly: true,
        printerWidth: mapping.printerWidth // Add printer width per mapping
      };
      
      if (useWebFallback) {
         if (config.orientation === 'thermal' && !window.bluetoothSerial) {
           generateRawEscPosReceipt(splitData, type, true, options).then(previewLines => {
             showTextDebugPreview(previewLines, options.printerWidth || config.thermalWidth || '58mm');
           }).catch(() => {});
         }
         const html = buildHtml(splitData, type, options);
         combinedHtml += html + '<div style="page-break-after: always; margin-bottom: 20px; border-bottom: 1px dashed #ccc;"></div>';
      } else {
         try {
           await new Promise((res, rej) => executeSinglePrint(splitData, type, res, rej, options));
           // Jeda singkat antar cetakan nota agar printer COM / USB / Bluetooth siap menerima data berikutnya
           await new Promise(r => setTimeout(r, 1200));
         } catch(e) {
           console.error('Failed to print to ' + mapping.name, e);
           failedMappings.push(mapping.name);
         }
      }
    }
    
    if (useWebFallback && combinedHtml) {
       const style = getReceiptPrintCss();
       const topBar = `<div class="actions no-print" style="margin: 16px auto; display: flex; align-items: center; justify-content: center; gap: 8px;"><button class="btn btn-close" onclick="window.close()" style="background: #64748b; color: #ffffff; border: none; border-radius: 6px; padding: 7px 16px; font-size: 12px; font-weight: 600; cursor: pointer;">Kembali</button><button class="btn btn-print" onclick="window.print()" style="background: #0f766e; color: #ffffff; border: none; border-radius: 6px; padding: 7px 16px; font-size: 12px; font-weight: 600; cursor: pointer;">Cetak</button></div>`;
       const fullHtml = `<!DOCTYPE html><html><head><title>Split Nota</title><style>${style}</style></head><body onload="window.print()">${topBar}${combinedHtml}</body></html>`;
       const receiptWindow = window.open('', '_blank');
       if (!receiptWindow) throw new Error('Popup printer diblokir oleh browser Anda.');
       receiptWindow.document.open();
       receiptWindow.document.write(fullHtml);
       receiptWindow.document.close();
       return { method: 'web', success: true };
    }
    
    if (failedMappings.length > 0) {
      throw new Error("Nota " + failedMappings.join(", ") + " gagal tercetak. Silakan periksa Pengaturan Printer.");
    }

    return { method: 'bluetooth', success: true };
  } else {
    return new Promise((resolve, reject) => {
      executeSinglePrint(receiptData, type, resolve, reject, {});
    });
  }
};

export const executePrintBoth = async (receiptDataTx, receiptDataOrd) => {
  const isKuliner = localStorage.getItem("PREVIEW_APPS") === "true";
  const config = getReceiptConfig();
  const hasSavedUsbPrinter = Boolean(getSavedUsbPrinter());
  const hasSavedSerialPrinter = Boolean(getSavedSerialPrinter());
  const hasSavedWindowsPrinter = Boolean(getSavedWindowsPrinter());
  const hasHardwarePrinter = Boolean(window.bluetoothSerial || hasSavedUsbPrinter || hasSavedSerialPrinter || hasSavedWindowsPrinter);

  if (isKuliner && !hasHardwarePrinter) {
    throw new Error("Printer belum terhubung. Silakan sambungkan printer di Pengaturan Printer.");
  }

  const useWebFallback = !isKuliner && (!hasHardwarePrinter);

  if (useWebFallback) {
    const htmlTx = buildReceiptHtml(receiptDataTx);
    const htmlOrd = buildOrderReceiptHtml(receiptDataOrd);
    const combinedHtml = `${htmlTx}<div style="page-break-after: always; margin-bottom: 20px; border-bottom: 1px dashed #ccc;"></div>${htmlOrd}`;
    const style = getReceiptPrintCss();
    const topBar = `<div class="actions no-print" style="margin: 16px auto; display: flex; align-items: center; justify-content: center; gap: 8px;"><button class="btn btn-close" onclick="window.close()" style="background: #64748b; color: #ffffff; border: none; border-radius: 6px; padding: 7px 16px; font-size: 12px; font-weight: 600; cursor: pointer;">Kembali</button><button class="btn btn-print" onclick="window.print()" style="background: #0f766e; color: #ffffff; border: none; border-radius: 6px; padding: 7px 16px; font-size: 12px; font-weight: 600; cursor: pointer;">Cetak</button></div>`;
    const fullHtml = `<!DOCTYPE html><html><head><title>Cetak Nota</title><style>${style}</style></head><body onload="window.print()">${topBar}${combinedHtml}</body></html>`;
    const receiptWindow = window.open('', '_blank');
    if (!receiptWindow) throw new Error('Popup printer diblokir oleh browser Anda.');
    receiptWindow.document.open();
    receiptWindow.document.write(fullHtml);
    receiptWindow.document.close();
    return { method: 'web', success: true };
  } else {
    // Bluetooth printing: print them sequentially
    try {
      await new Promise((resolve, reject) => {
        executeSinglePrint(receiptDataTx, 'transaction', resolve, reject, {});
      });
      // Wait 2.5 seconds to let the printer finish printing the first receipt
      await new Promise(resolve => setTimeout(resolve, 2500));
      await executePrint(receiptDataOrd, 'order');
      return { method: 'bluetooth', success: true };
    } catch (e) {
      console.error('Sequential bluetooth printing failed:', e);
      throw e;
    }
  }
};

const executeBluetoothWrite = async (receiptData, type, resolve, reject, options = {}) => {
  try {
    const buffer = await generateRawEscPosReceipt(receiptData, type, false, options);
    window.bluetoothSerial.write(
      buffer,
      () => resolve({ method: 'bluetooth', success: true }),
      (err) => reject(new Error("Gagal mengirim data ke printer: " + err))
    );
  } catch(e) {
    reject(new Error("Error saat membuat format cetak: " + e.message));
  }
};

// Cache sederhana di memori untuk menyimpan gambar base64 logo toko agar tidak download berkali-kali
const logoCache = {};

const getBase64Image = async (url) => {
  if (!url) return '';
  if (url.startsWith('data:')) return url;
  
  // Jika sudah ada di cache, langsung kembalikan instan tanpa download ulang
  if (logoCache[url]) {
    return logoCache[url];
  }
  
  // Coba download biasa tanpa cache buster dulu agar memanfaatkan cache bawaan perangkat
  let targetUrl = url;
  
  // 1. Jika berjalan di Android APK (Capacitor), gunakan CapacitorHttp yang bisa bypass CORS
  if (Capacitor.isNativePlatform()) {
    try {
      const response = await CapacitorHttp.get({
        url: targetUrl,
        responseType: 'blob'
      });
      if (response.data) {
        const base64Data = `data:image/png;base64,${response.data}`;
        logoCache[url] = base64Data; // simpan di cache
        return base64Data;
      }
    } catch (e) {
      console.warn("CapacitorHttp failed, trying with cache buster:", e);
      // Jika gagal, coba gunakan cache buster t=timestamp sebagai fallback
      targetUrl = url + (url.includes('?') ? '&' : '?') + 't=' + new Date().getTime();
      try {
        const responseBust = await CapacitorHttp.get({
          url: targetUrl,
          responseType: 'blob'
        });
        if (responseBust.data) {
          const base64Data = `data:image/png;base64,${responseBust.data}`;
          logoCache[url] = base64Data;
          return base64Data;
        }
      } catch (errBust) {
        console.error("CapacitorHttp fallback failed:", errBust);
      }
    }
  }

  // 2. Fallback untuk web: coba fetch biasa
  try {
    const response = await fetch(targetUrl, { mode: 'cors' });
    const blob = await response.blob();
    const result = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve('');
      reader.readAsDataURL(blob);
    });
    if (result) {
      logoCache[url] = result; // simpan di cache
    }
    return result || targetUrl;
  } catch (err) {
    // S3 publik sering tidak mengizinkan CORS. Ambil ulang melalui backend
    // terautentikasi agar canvas dapat membaca pixel logo secara aman.
    try {
      const parsedUrl = new URL(url, window.location.origin);
      const storageKey = parsedUrl.pathname.replace(/^\/+/, '');
      if (storageKey) {
        const blob = await apiClient.getBlob(`/storage/download?key=${encodeURIComponent(storageKey)}`);
        const result = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => resolve('');
          reader.readAsDataURL(blob);
        });
        if (result) {
          logoCache[url] = result;
          return result;
        }
      }
    } catch (proxyErr) {
      console.warn('Logo tidak dapat diambil melalui API storage:', proxyErr);
    }
    return targetUrl;
  }
};

const buildHtml = (receiptData, type, options = {}) => {
  return type === 'transaction'
    ? buildReceiptHtml(receiptData)
    : type === 'shift'
      ? buildShiftReportHtml(receiptData)
      : type === 'customer_qr'
        ? buildCustomerQRReceiptHtml(receiptData)
        : type === 'return'
          ? buildReturnReceiptHtml(receiptData)
          : buildOrderReceiptHtml(receiptData, options);
};

// Mengonversi gambar logo (foto/gradasi) menjadi hitam-putih tegas (threshold).
// Ini krusial untuk cetak thermal via driver Windows karena driver generic printer
// thermal seringkali men-dither gambar grayscale/berwarna dengan algoritma kasar
// sehingga logo terlihat seperti noise/kode-kode acak. Dengan logo yang sudah
// murni hitam-putih sebelum dikirim, driver tinggal mencetak apa adanya.
const convertImageToMonochromeDataUrl = (imageUrl, threshold = 150) => {
  return new Promise((resolve) => {
    if (!imageUrl) return resolve(imageUrl);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const targetWidth = 240;
        const ratio = (img.naturalWidth || img.width || targetWidth) > 0
          ? targetWidth / (img.naturalWidth || img.width)
          : 1;
        const w = targetWidth;
        const h = Math.max(1, Math.round((img.naturalHeight || img.height || targetWidth) * ratio));

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);

        const imgData = ctx.getImageData(0, 0, w, h);
        const px = imgData.data;
        for (let i = 0; i < px.length; i += 4) {
          const alpha = px[i + 3];
          // Luminance standar; pixel transparan dianggap putih (background nota).
          const luminance = alpha === 0
            ? 255
            : (0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]);
          const value = luminance < threshold ? 0 : 255;
          px[i] = value;
          px[i + 1] = value;
          px[i + 2] = value;
          px[i + 3] = 255;
        }
        ctx.putImageData(imgData, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        console.warn('Gagal konversi logo ke monochrome, pakai gambar asli:', e);
        resolve(imageUrl);
      }
    };
    img.onerror = () => resolve(imageUrl);
    img.src = imageUrl;
  });
};

const convertLogoToBase64 = async (receiptData) => {
  const logoUrl = receiptData.storeLogoUrl;
  if (!logoUrl) return receiptData;

  const config = getReceiptConfig();
  const isThermal = config.orientation === 'thermal';

  const base64 = logoUrl.startsWith('data:') ? logoUrl : await getBase64Image(logoUrl);
  if (!base64) return receiptData;

  // Jangan masukkan URL lintas domain ke canvas. Jika fetch logo gagal karena
  // CORS, getBase64Image mengembalikan URL asal dan canvas akan menjadi tainted.
  // Nota tetap harus bisa dicetak raw USB, jadi gunakan data logo asal tanpa
  // memaksa proses canvas yang dapat menghentikan seluruh proses cetak.
  if (!base64.startsWith('data:image/')) {
    return receiptData;
  }

  // Khusus cetak thermal (Windows/web fallback), logo dipaksa hitam-putih tegas
  // supaya tidak didither jelek oleh driver printer generic.
  if (isThermal) {
    const mono = await convertImageToMonochromeDataUrl(base64);
    return { ...receiptData, storeLogoUrl: mono || base64 };
  }

  return { ...receiptData, storeLogoUrl: base64 };
};

const doWebPrintFallback = async (receiptData, type, resolve, reject, options = {}) => {
  const isKuliner = localStorage.getItem("PREVIEW_APPS") === "true";
  if (isKuliner) {
    if (reject) reject(new Error("Printer belum terhubung. Silakan periksa Pengaturan Printer."));
    return;
  }
  try {
    const processedData = await convertLogoToBase64(receiptData);
    const html = buildHtml(processedData, type, options);

    let receiptWindow = null;
    try {
      receiptWindow = window.open("", "_blank");
    } catch (e) {
      receiptWindow = null;
    }

    if (receiptWindow) {
      receiptWindow.document.open();
      receiptWindow.document.write(html);
      receiptWindow.document.close();
      if (resolve) resolve({ method: 'web', success: true });
      return;
    }

    // Fallback untuk web (bila window.open diblokir popup blocker karena dipanggil secara async setelah API call)
    const printConfig = getReceiptConfig();
    const isThermalReceipt = printConfig.orientation === 'thermal';
    const iframeWidth = isThermalReceipt ? (printConfig.thermalWidth || '80mm') : '210mm';
    const iframeHeight = isThermalReceipt ? '297mm' : '148mm';

    let iframe = document.getElementById("asalink-print-iframe");
    if (!iframe) {
      iframe = document.createElement("iframe");
      iframe.id = "asalink-print-iframe";
      iframe.style.position = "fixed";
      iframe.style.left = "-10000px";
      iframe.style.top = "-10000px";
      iframe.style.border = "0";
      iframe.style.visibility = "hidden";
      document.body.appendChild(iframe);
    }
    iframe.style.width = iframeWidth;
    iframe.style.height = iframeHeight;

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(html);
    iframeDoc.close();

    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        if (resolve) resolve({ method: 'web', success: true });
      } catch (err) {
        console.error("Iframe print failed:", err);
        if (reject) reject(err);
      }
    }, 300);
  } catch (err) {
    if (reject) reject(err);
  }
};

// ============================================================================
// RAW ESC/POS BUILDERS (100% PRECISE TEXT)
// ============================================================================

// Mock encoder untuk menampilkan preview bentuk teks murni di browser
class MockEncoder {
  constructor(cpl) { 
    this.output = []; 
    this.cpl = cpl || 32;
    this.alignment = 'left';
    this.isLarge = false;
  }
  initialize() { return this; }
  align(val) { this.alignment = val; return this; }
  raw() { return this; }
  pulse() { return this; }
  
  _esc(text) {
    let t = String(text || '');
    return t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\x1E/g, '');
  }

  line(text) { 
    if (this.output.length === 0) this.output.push('');
    const t = this._esc(String(text || ''));
    let res = this.isBold ? `<b>${t}</b>` : t;
    if (this.isLarge) res = `<span style="font-size:1.2em;">${res}</span>`;
    this.output[this.output.length - 1] += res; 
    this.output.push(""); // cursor moves to next line
    return this; 
  }
  text(text) { 
    if (this.output.length === 0) this.output.push('');
    const t = this._esc(String(text || ''));
    let res = this.isBold ? `<b>${t}</b>` : t;
    if (this.isLarge) res = `<span style="font-size:1.2em;">${res}</span>`;
    this.output[this.output.length - 1] += res; 
    return this; 
  }
  bold(val) { this.isBold = val; return this; }
  size(w, h) {
    if (w === 'normal') {
      this.isLarge = false;
    } else if (w > 1 || h > 1) {
      this.isLarge = true;
    } else {
      this.isLarge = false;
    }
    return this;
  }
  newline(count = 1) {
    const n = Math.max(0, Number(count) || 1);
    for (let i = 0; i < n; i++) this.output.push("");
    return this;
  }
  formFeed() {
    this.output.push("--- [PINDAH KE SOBEKAN KERTAS BERIKUTNYA] ---");
    return this;
  }
  cut() { return this; }
  image() { 
    const str = "[GAMBAR LOGO MUNCUL DI SINI]";
    const spaces = Math.floor((this.cpl - str.length) / 2);
    const padded = ' '.repeat(spaces > 0 ? spaces : 0) + str;
    this.output.push(padded); 
    return this; 
  }
  encode() { return this.output; }
}

class RawHorizontalReceiptEncoder {
  constructor(cpl = 80) {
    this.cpl = cpl;
    this.buffer = [];
    this.textEncoder = new TextEncoder();
  }
  initialize() {
    // 1. ESC @ (0x1B, 0x40): Reset printer to clean default state
    this.buffer.push(0x1B, 0x40);
    // 2. ESC 2 (0x1B, 0x32): Set line spacing to standard 1/6 inch (6 lines per inch)
    this.buffer.push(0x1B, 0x32);
    // 3. ESC C 33 (0x1B, 0x43, 33): Set page length to 33 lines (exactly 5.5 inches = 1 sobekan kertas continuous form A5)
    this.buffer.push(0x1B, 0x43, 33);
    // 4. ESC U 0 (0x1B, 0x55, 0x00): Set cetak dua arah bolak-balik (bidirectional) agar kecepatan gerak jarum maksimal
    this.buffer.push(0x1B, 0x55, 0x00);
    return this;
  }
  pulse() {
    // ESC p 0 25 250 (Pin 2) & ESC p 1 25 250 (Pin 5) - Buka Laci Kasir RJ11
    this.buffer.push(0x1B, 0x70, 0x00, 0x19, 0xFA);
    this.buffer.push(0x1B, 0x70, 0x01, 0x19, 0xFA);
    return this;
  }
  bold(enable = true) {
    if (enable) {
      // ESC E (Emphasized ON) + ESC G (Double-strike ON) for maximum dark & sharp text on Dot Matrix
      this.buffer.push(0x1B, 0x45, 0x1B, 0x47);
    } else {
      // ESC F (Emphasized OFF) + ESC H (Double-strike OFF)
      this.buffer.push(0x1B, 0x46, 0x1B, 0x48);
    }
    return this;
  }
  line(text = "") {
    const bytes = this.textEncoder.encode(String(text || "") + "\r\n");
    for (let i = 0; i < bytes.length; i++) {
      this.buffer.push(bytes[i]);
    }
    return this;
  }
  newline(count = 1) {
    const n = Math.max(0, Number(count) || 1);
    for (let i = 0; i < n; i++) {
      this.buffer.push(0x0D, 0x0A);
    }
    return this;
  }
  formFeed() {
    // 0x0C (Form Feed): feeds paper until the 33-line perforation boundary of the current sobekan
    this.buffer.push(0x0C);
    return this;
  }
  cut() {
    // 0x0C (Form Feed): feeds paper until the 33-line perforation boundary of the current sobekan
    this.buffer.push(0x0C);
    // ESC @ (0x1B, 0x40): Reset and return printer to Ready/Standby mode so front panel buttons (LF/FF, micro-feed) unlock immediately
    this.buffer.push(0x1B, 0x40);
    return this;
  }
  encode() {
    return new Uint8Array(this.buffer);
  }
}

const showTextDebugPreview = (lines, paperWidth) => {
  // HIDDEN: popup simulasi dinonaktifkan sementara (printer thermal sudah tersedia).
  // Untuk mengembalikan, hapus baris return di bawah ini.
  return;
  const existing = document.getElementById('bluetooth-print-debug-preview');
  if (existing) existing.remove();
  
  const backdrop = document.createElement('div');
  backdrop.id = 'bluetooth-print-debug-preview';
  backdrop.style.position = 'fixed';
  backdrop.style.inset = '0';
  backdrop.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';
  backdrop.style.zIndex = '99999';
  backdrop.style.display = 'flex';
  backdrop.style.alignItems = 'center';
  backdrop.style.justifyContent = 'center';
  backdrop.style.padding = '20px';
  backdrop.style.backdropFilter = 'blur(4px)';
  backdrop.style.fontFamily = 'sans-serif';
  
  const modal = document.createElement('div');
  modal.style.backgroundColor = '#ffffff';
  modal.style.borderRadius = '16px';
  modal.style.padding = '20px';
  modal.style.width = '100%';
  modal.style.maxWidth = '380px';
  modal.style.maxHeight = '85vh';
  modal.style.display = 'flex';
  modal.style.flexDirection = 'column';
  modal.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)';
  
  const header = document.createElement('div');
  header.style.display = 'flex';
  header.style.justifyContent = 'space-between';
  header.style.alignItems = 'center';
  header.style.marginBottom = '10px';
  
  const title = document.createElement('h3');
  title.innerText = 'Simulasi Teks Bluetooth';
  title.style.margin = '0';
  title.style.fontSize = '14px';
  title.style.fontWeight = 'bold';
  
  const closeBtn = document.createElement('button');
  closeBtn.innerText = 'Tutup';
  closeBtn.style.padding = '5px 10px';
  closeBtn.style.backgroundColor = '#f3f4f6';
  closeBtn.style.border = 'none';
  closeBtn.style.borderRadius = '6px';
  closeBtn.style.cursor = 'pointer';
  closeBtn.style.fontSize = '11px';
  closeBtn.style.fontWeight = 'bold';
  closeBtn.onclick = () => backdrop.remove();
  
  header.appendChild(title);
  header.appendChild(closeBtn);
  
  const infoText = document.createElement('p');
  infoText.innerText = `Simulasi teks ESC/POS ${paperWidth} (Spasi presisi)`;
  infoText.style.fontSize = '10px';
  infoText.style.color = '#6b7280';
  infoText.style.margin = '0 0 12px 0';
  
  const canvasContainer = document.createElement('div');
  canvasContainer.style.overflowY = 'auto';
  canvasContainer.style.flex = '1';
  canvasContainer.style.backgroundColor = '#fdfdfd';
  canvasContainer.style.padding = '12px';
  canvasContainer.style.borderRadius = '8px';
  canvasContainer.style.border = '1px dashed #ccc';
  canvasContainer.style.display = 'flex';
  canvasContainer.style.justifyContent = 'center';
  
  const pre = document.createElement('pre');
  pre.style.margin = '0';
  pre.style.padding = '0';
  pre.style.fontFamily = "'Courier New', Courier, monospace";
  pre.style.fontSize = paperWidth === '80mm' ? '11px' : '12px';
  pre.style.lineHeight = '1.2';
  pre.style.color = '#000';
  pre.style.whiteSpace = 'pre';
  
  pre.innerHTML = lines.join('\n');
  
  canvasContainer.appendChild(pre);
  
  modal.appendChild(header);
  modal.appendChild(infoText);
  modal.appendChild(canvasContainer);
  backdrop.appendChild(modal);
  document.body.appendChild(backdrop);
};

const formatNumber = (n) => {
  const num = Number(n) || 0;
  return num.toLocaleString("id-ID");
};

// Gunakan kolom uang dengan posisi tanda, "Rp", dan nominal yang tetap.
// Ini membuat baris adjustment tetap sejajar pada printer thermal 58/80 mm.
export const formatReceiptMoneyColumn = (amount, sign = "", width = 12) => {
  const prefix = `${sign || " "} Rp `;
  const nominal = formatNumber(amount);
  const safeWidth = Math.max(width, prefix.length + nominal.length);
  return `${prefix}${nominal.padStart(safeWidth - prefix.length, " ")}`;
};

export const formatReceiptMoneyRows = (label, amount, sign = "", cpl = 32, valueWidth = 12) => {
  const value = formatReceiptMoneyColumn(amount, sign, valueWidth);
  const maxLabelWidth = Math.max(1, cpl - value.length);
  const safeLabel = String(label || "");
  if (safeLabel.length > maxLabelWidth) {
    return [safeLabel.slice(0, cpl), value.padStart(cpl, " ")];
  }
  return [`${safeLabel.padEnd(maxLabelWidth, " ")}${value}`];
};

const formatReceiptDate = (raw) => {
  if (!raw || raw === "-") return "-";
  const months = {
    jan: "Jan", feb: "Feb", mar: "Mar", apr: "Apr", mei: "Mei", may: "Mei",
    jun: "Jun", june: "Jun", jul: "Jul", agu: "Agu", ags: "Agu", august: "Agu",
    sep: "Sep", september: "Sep", okt: "Okt", october: "Okt", nov: "Nov",
    november: "Nov", des: "Des", december: "Des", january: "Jan", february: "Feb",
    march: "Mar", april: "Apr", july: "Jul"
  };
  const str = String(raw).trim();
  const textMatch = str.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})/);
  if (textMatch) {
    const day = textMatch[1].padStart(2, "0");
    const month = months[textMatch[2].toLowerCase()] || textMatch[2].slice(0, 3);
    return `${day} ${month} ${textMatch[3]}`;
  }
  const d = new Date(raw);
  if (!isNaN(d.getTime())) {
    const month = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"][d.getMonth()];
    return `${String(d.getDate()).padStart(2, "0")} ${month} ${d.getFullYear()}`;
  }
  return str;
};

const splitDateTime = (raw) => {
  if (!raw) return { date: "-", time: "" };
  let str = String(raw).trim().replace(/\s+[pP]ukul\s+/g, ' ').replace(/\b[pP]ukul\b/g, '').trim();
  let d = str;
  let t = "";
  
  if (str.includes(",")) {
    const parts = str.split(",").map((s) => s.trim());
    d = parts[0] || "-";
    t = parts[1] || "";
  } else {
    const match = str.match(/^(.*?)\s+(\d{2}[.:]\d{2}(?::\d{2})?)$/);
    if (match) {
      d = match[1].trim() || "-";
      t = match[2].trim() || "";
    }
  }
  return { date: formatReceiptDate(d), time: t };
};

export const getLoggedInUserName = () => {
  try {
    const raw = localStorage.getItem("user_data");
    const parsed = raw ? JSON.parse(raw) : null;
    
    // Prioritas UTAMA: Nama pengguna yang sedang login saat ini
    const loggedInName = parsed?.full_name || parsed?.name || localStorage.getItem("username") || parsed?.email;
    if (loggedInName && loggedInName.trim()) return loggedInName.trim();

    // Fallback: Nama kasir dari shift aktif jika user_data kosong
    const shiftCashier = localStorage.getItem("activeShiftCashierName");
    if (shiftCashier && shiftCashier.trim()) return shiftCashier.trim();

    return "";
  } catch {
    return localStorage.getItem("username") || "";
  }
};

// Fungsi untuk meload logo ke canvas yang lebarnya pas dengan kertas
const loadLogoToCanvas = async (base64Url, paperWidth) => {
  return new Promise((resolve) => {
    if (!base64Url) return resolve(null);
    const targetWidth = paperWidth === '80mm' ? 576 : 384;
    
    const img = new Image();
    img.onload = () => {
      const sourceWidth = img.naturalWidth || img.width;
      const sourceHeight = img.naturalHeight || img.height;
      const maxLogoWidth = Math.floor(targetWidth * 0.5);

      // Deteksi batas vertikal isi logo saja. Lebar asli dipertahankan supaya
      // logo tidak mengecil, sementara ruang putih atas dan bawah dibuang.
      let cropTop = 0;
      let cropBottom = sourceHeight - 1;
      try {
        const sourceCanvas = document.createElement('canvas');
        sourceCanvas.width = sourceWidth;
        sourceCanvas.height = sourceHeight;
        const sourceCtx = sourceCanvas.getContext('2d');
        sourceCtx.clearRect(0, 0, sourceWidth, sourceHeight);
        sourceCtx.drawImage(img, 0, 0);
        const pixels = sourceCtx.getImageData(0, 0, sourceWidth, sourceHeight).data;
        let minY = sourceHeight;
        let maxY = -1;
        for (let y = 0; y < sourceHeight; y += 1) {
          for (let x = 0; x < sourceWidth; x += 1) {
            const index = (y * sourceWidth + x) * 4;
            const alpha = pixels[index + 3];
            const luminance = 0.299 * pixels[index] + 0.587 * pixels[index + 1] + 0.114 * pixels[index + 2];
            if (alpha > 18 && luminance < 248) {
              minY = Math.min(minY, y);
              maxY = Math.max(maxY, y);
            }
          }
        }
        if (maxY >= minY) {
          cropTop = Math.max(0, minY - 2);
          cropBottom = Math.min(sourceHeight - 1, maxY + 2);
        }
      } catch {
        // Jika crop tidak tersedia, gunakan gambar penuh tanpa menggagalkan cetak.
      }

      const scale = Math.min(maxLogoWidth / sourceWidth, 1);
      const w = Math.max(8, Math.round((sourceWidth * scale) / 8) * 8);
      const croppedHeight = Math.max(1, cropBottom - cropTop + 1);
      const h = Math.max(8, Math.round((croppedHeight * scale) / 8) * 8);

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, targetWidth, h);

      const x = Math.floor((targetWidth - w) / 2);
      ctx.drawImage(img, 0, cropTop, sourceWidth, croppedHeight, x, 0, w, h);
      resolve(canvas);
    };
    img.onerror = () => resolve(null);
    img.src = base64Url;
  });
};

/**
 * Membangun buffer byte raw text untuk Nota Horizontal A5 Landscape (80 kolom).
 * Menggunakan format tabel 80 kolom terstruktur dengan garis pembatas tabel,
 * kop toko di kiri, info pelanggan di kanan, dan rincian harga di bawah.
 * Menghasilkan cetakan yang 100% tajam pada printer dot matrix, spooler, atau raw printer.
 */
const generateRawHorizontalReceipt = async (data, type, forPreview = false, options = {}) => {
  const config = getReceiptConfig();
  const isKuliner = localStorage.getItem("PREVIEW_APPS") === "true";
  const cutFeedLines = Math.max(0, Number(options.cutFeedLines || 0));
  const cpl = 80;
  const doubleLine = '='.repeat(cpl);
  const dashLine = '-'.repeat(cpl);

  const encoder = forPreview ? new MockEncoder(cpl) : new RawHorizontalReceiptEncoder(cpl);
  encoder.initialize();

  // Buka Laci Kasir via kabel RJ11 jika diaktifkan atau diminta eksplisit
  if (!forPreview && (options.openCashDrawer || (type === 'transaction' && config.autoOpenCashDrawer))) {
    encoder.pulse();
  }

  // Parameter pagination Continuous Form A5 (Tinggi 14 cm = 5.5 inci = 33 baris pada 6 LPI)
  const PAGE_TOTAL_LINES = 33;
  const BOTTOM_MARGIN_LINES = 4; // 4 baris batas bawah kosong sebelum sobekan kertas agar teks tidak terpotong
  const MAX_PAGE_LINES = PAGE_TOTAL_LINES - BOTTOM_MARGIN_LINES; // 29 baris aktif maksimal per halaman
  const TOP_GAP_LINES = 2; // 2 baris jarak kosong di atas kertas lanjutan sama seperti posisi atas kop kertas pertama

  const pad2Col = (leftText, rightText, col1 = 40, col2 = 40) => {
    const l = String(leftText || '').padEnd(col1, ' ').substring(0, col1);
    const r = String(rightText || '').padEnd(col2, ' ').substring(0, col2);
    return l + r;
  };

  const formatMetaRight = (label, val) => {
    if (!label && !val) return '';
    const lbl = String(label || '').padEnd(16, ' ').substring(0, 16);
    const v = String(val || '').padEnd(22, ' ').substring(0, 22);
    return `${lbl}: ${v}`;
  };

  if (type === 'transaction') {
    const {
      storeName = "Toko",
      storeAddress = "",
      storeTagline = "",
      storePhone = "",
      storeFooterNote = "Terima Kasih Telah Menjadi Pelanggan Kami",
      meta = {},
      items = [],
      totals = {},
    } = data || {};

    const { date: trxDate, time: trxTime } = splitDateTime(meta.transactionDateTime);
    const transactionNumber = meta.transactionNumber || "-";
    const cleanTxNum = formatSimplifiedTxNum(transactionNumber);
    const fullDateTime = trxDate !== "-"
      ? `${trxDate}${trxTime ? `, ${trxTime}` : ""}`
      : (trxTime || "-");
    const cashierName = getLoggedInUserName() || "-";
    const customerName = meta.customerName || "Pelanggan Umum";
    const transactionNotes = meta.transactionNotes || "";
    const sellerType = meta.sellerType || meta.seller_type || "";
    const tableName = meta.tableName || meta.table_name || meta.table?.name || (typeof meta.table === "string" ? meta.table : "");
    const unitName = detectVehicleUnit(meta, items, data);

    let currentLineCount = 0;

    const goToNextPage = () => {
      encoder.formFeed();
      encoder.newline(TOP_GAP_LINES);
      encoder.line(dashLine);
      encoder.line("No  Nama Produk                           Qty     Harga Satuan     Harga Total  ");
      encoder.line(dashLine);
      currentLineCount = TOP_GAP_LINES + 3;
    };

    // 1. Header: Store info (left 40) + Customer / Tx info (right 40)
    const headerLeft = [
      String(storeName || '').trim(),
      String(storeAddress || '').trim(),
      storePhone ? String(storePhone).replace(/^handphone:\s*/i, "Telp: ") : '',
      storeTagline ? String(storeTagline).trim() : '',
      cashierName ? `Kasir: ${cashierName}` : '',
    ].filter(Boolean);

    const headerRight = [
      formatMetaRight("Nama Pelanggan", customerName),
      unitName && String(unitName).trim() ? formatMetaRight("Unit", String(unitName).trim()) : '',
      tableName && isKuliner ? formatMetaRight("Meja", tableName) : '',
      formatMetaRight("No. Transaksi", cleanTxNum),
      formatMetaRight("Tanggal & Waktu", fullDateTime),
      sellerType ? formatMetaRight("Tipe Penjualan", sellerType) : '',
    ].filter(Boolean);

    const headerRows = Math.max(headerLeft.length, headerRight.length);
    encoder.bold(true);
    for (let i = 0; i < headerRows; i++) {
      encoder.line(pad2Col(headerLeft[i] || '', headerRight[i] || ''));
    }
    currentLineCount += headerRows;

    // 2. Table Header
    encoder.line(doubleLine);
    encoder.line("No  Nama Produk                           Qty     Harga Satuan     Harga Total  ");
    encoder.line(dashLine);
    currentLineCount += 3;

    // Persiapan fungsi baris item produk
    const getItemLines = (it, idx) => {
      const lines = [];
      const noStr = String(idx + 1).padStart(2, ' ') + '  '; // 4 chars
      let name = it.name || '-';
      if (it.variant_name) name += ` (${it.variant_name})`;
      const unit = getPhysicalProductUnit(it, unitName, isKuliner);
      const qtyStr = (String(it.qty || 0) + (unit ? ` ${unit}` : '')).padStart(7, ' ') + ' '; // 8 chars
      const priceStr = (`Rp ${formatNumber(it.price || 0)}`).padStart(15, ' ') + ' '; // 16 chars
      const calculatedItem = calculateOrderItemTotals(it);
      const totalIt = Number(it.total ?? it.total_price ?? calculatedItem.subtotal);
      const itemDiscount = Number(it.discount_amount || 0);
      const itemAdditionalCost = Number(it.additional_cost || 0);
      const baseTotal = calculatedItem.baseSubtotal;
      const displayedLineTotal = itemDiscount > 0 || itemAdditionalCost > 0 ? baseTotal : totalIt;
      const totalStr = (`Rp ${formatNumber(displayedLineTotal)}`).padStart(15, ' ') + ' '; // 16 chars

      if (name.length <= 34) {
        lines.push(noStr + name.padEnd(36, ' ') + qtyStr + priceStr + totalStr);
      } else {
        const w1 = name.substring(0, 34);
        const w2 = name.substring(34, 68);
        lines.push(noStr + w1.padEnd(36, ' ') + qtyStr + priceStr + totalStr);
        lines.push('    ' + w2.padEnd(36, ' ') + ' '.repeat(40));
      }

      const itemNotes = !isKuliner ? (it.notes || it.note || '') : '';
      if (itemNotes) {
        lines.push('    * ' + String(itemNotes).substring(0, 74));
      }

      if (itemDiscount > 0) {
        lines.push(...formatReceiptMoneyRows('Diskon Item', itemDiscount, '-', cpl, 16));
      }
      if (itemAdditionalCost > 0) {
        lines.push(...formatReceiptMoneyRows('Biaya Tambahan Item', itemAdditionalCost, '+', cpl, 16));
      }
      if (itemDiscount > 0 || itemAdditionalCost > 0) {
        lines.push(...formatReceiptMoneyRows('Total Item', totalIt, '', cpl, 16));
      }

      if (it.addons && it.addons.length > 0) {
        it.addons.forEach(ad => {
          const adName = `+ ${ad.name}`;
          const adQty = `${ad.qty} `;
          const adPrice = `Rp ${formatNumber(ad.price || 0)}`;
          const adTotal = `Rp ${formatNumber(ad.total || 0)}`;
          lines.push('    ' + adName.padEnd(36, ' ') + adQty.padStart(8, ' ') + adPrice.padStart(16, ' ') + adTotal.padStart(16, ' '));
        });
      }
      return lines;
    };

    // 5. Summary & Footer (2 Columns: 40 left, 40 right)
    const subtotal = Number(totals.subtotal ?? 0);
    const total = Number(totals.total ?? subtotal);
    const tunai = Number(totals.tunai ?? total);
    const change = Number(totals.change ?? 0);
    const isDP = !!totals.isDownPayment;
    const remaining = Number(totals.remaining ?? 0);
    const dpAmount = Number(totals.dpAmount ?? 0);
    const discount = Number(totals.discount ?? totals.discountTotal ?? 0);
    const additionalCosts = Number(totals.additionalCosts ?? totals.additional_costs ?? totals.additional ?? 0);
    const paymentMethodLabel = getReceiptPaymentMethodLabel(totals);
    const isDiscountFromType = meta.salesTypeObj
      ? meta.salesTypeObj.jenis === "Diskon" || meta.salesTypeObj.jenis === "Markup"
      : !!(
          meta.salesTypeObj?.jenis === "Diskon" ||
          meta.salesTypeObj?.jenis === "Markup" ||
          (typeof meta.sellerType === "object" && (meta.sellerType?.jenis === "Diskon" || meta.sellerType?.jenis === "Markup"))
        );

    const { discountLabel, additionalCostsLabel } = getReceiptAdjustmentLabels(data);
    const paymentStatus = isDP
      ? (remaining <= 0 ? "LUNAS (DP)" : "BELUM LUNAS (DP)")
      : (tunai < total ? "BELUM LUNAS" : "LUNAS");

    const formatSummaryRow = (lbl, valStr) => {
      const l = String(lbl || '').padEnd(20, ' ').substring(0, 20);
      const v = String(valStr || '').padStart(18, ' ').substring(0, 18);
      return `${l}: ${v}`;
    };

    const rightSummary = [
      formatSummaryRow("Sub Total", `Rp ${formatNumber(subtotal)}`),
      additionalCosts > 0 && !isDiscountFromType ? formatSummaryRow(additionalCostsLabel, `Rp ${formatNumber(additionalCosts)}`) : '',
      discount > 0 && !isDiscountFromType ? formatSummaryRow(discountLabel, `- Rp ${formatNumber(discount)}`) : '',
      formatSummaryRow("Grand Total", `Rp ${formatNumber(total)}`),
      dpAmount > 0 ? formatSummaryRow("DP", `Rp ${formatNumber(dpAmount)}`) : '',
      isDP && remaining > 0 ? formatSummaryRow("Sisa Tagihan", `Rp ${formatNumber(remaining)}`) : '',
      change > 0 && !isDP ? formatSummaryRow("Kembalian", `Rp ${formatNumber(change)}`) : '',
      formatSummaryRow("Metode Pembayaran", paymentMethodLabel),
      formatSummaryRow("Status Pembayaran", paymentStatus),
    ].filter(Boolean);

    const leftFooter = [
      "Hormat Kami,",
      "",
      cashierName ? `( ${cashierName} )` : "( ____________________ )",
      String(storeFooterNote || 'Terima Kasih Telah Menjadi Pelanggan Kami').substring(0, 38),
      transactionNotes ? `Catatan: ${String(transactionNotes).substring(0, 30)}` : '',
      "Powered by ASAlink",
    ].filter(Boolean);

    const footerRows = Math.max(leftFooter.length, rightSummary.length);
    const getFooterLines = () => {
      const fLines = [];
      fLines.push(doubleLine);
      for (let i = 0; i < footerRows; i++) {
        const l = (leftFooter[i] || '').padEnd(40, ' ').substring(0, 40);
        const r = (rightSummary[i] || '').padEnd(40, ' ').substring(0, 40);
        fLines.push(l + r);
      }
      return fLines;
    };

    // 3. Render Items dengan Paginasi Otomatis
    const grouped = groupItems(items);
    if (grouped && grouped.length > 0) {
      const footerLinesCount = getFooterLines().length;
      grouped.forEach((it, idx) => {
        const itemLines = getItemLines(it, idx);
        const isLast = (idx === grouped.length - 1);
        const needLines = isLast ? (itemLines.length + footerLinesCount) : itemLines.length;

        if (currentLineCount + needLines > MAX_PAGE_LINES && (currentLineCount + itemLines.length > MAX_PAGE_LINES || isLast)) {
          goToNextPage();
        }

        itemLines.forEach(l => encoder.line(l));
        currentLineCount += itemLines.length;
      });
    } else {
      encoder.line(pad2Col('Tidak ada item', '', 40, 40));
      currentLineCount += 1;
    }

    // 4. Render Footer & Summary
    const footerLines = getFooterLines();
    if (currentLineCount + footerLines.length > MAX_PAGE_LINES) {
      goToNextPage();
    }
    footerLines.forEach(l => encoder.line(l));
    currentLineCount += footerLines.length;
    encoder.bold(false);
  } else if (type === 'order') {
    const {
      storeName = "Toko",
      meta = {},
      items = [],
    } = data || {};

    const transactionNumber = meta.transactionNumber || "-";
    const cleanTxNum = formatSimplifiedTxNum(transactionNumber);
    const cashierName = getLoggedInUserName() || "-";
    const customerName = meta.customerName || "Pelanggan Umum";
    const tableName = meta.tableName || meta.table_name || "";
    const title = options.customTitle || "NOTA PESANAN";

    let currentLineCount = 0;

    const goToNextPageOrder = () => {
      encoder.formFeed();
      encoder.newline(TOP_GAP_LINES);
      encoder.line(dashLine);
      encoder.line("No  Nama Item Pesanan                     Qty    Catatan");
      encoder.line(dashLine);
      currentLineCount = TOP_GAP_LINES + 3;
    };

    encoder.bold(true);
    encoder.line(pad2Col(title, `No: ${cleanTxNum}`));
    encoder.line(pad2Col(`Toko: ${storeName}`, `Pelanggan: ${customerName}`));
    currentLineCount = 2;
    if (tableName) {
      encoder.line(pad2Col(`Meja: ${tableName}`, `Kasir: ${cashierName}`));
      currentLineCount = 3;
    }
    encoder.line(doubleLine);
    encoder.line("No  Nama Item Pesanan                     Qty    Catatan");
    encoder.line(dashLine);
    currentLineCount += 3;

    const getOrderItemLines = (it, idx) => {
      const lines = [];
      const noStr = String(idx + 1).padStart(2, ' ') + '  ';
      let name = it.name || it.item_name || it.product_name || "-";
      if (it.variant_name) name += ` (${it.variant_name})`;
      const qty = Number(it.qty || it.quantity || 0);
      const qtyStr = String(qty).padStart(6, ' ') + ' ';
      const noteStr = it.notes || it.note || "";
      lines.push(noStr + name.padEnd(36, ' ') + qtyStr + String(noteStr).substring(0, 34));

      if (it.addons && it.addons.length > 0) {
        it.addons.forEach(ad => {
          lines.push(`    + ${ad.name} (x${ad.qty})`);
        });
      }
      return lines;
    };

    const grouped = groupItems(items);
    grouped.forEach((it, idx) => {
      const itemLines = getOrderItemLines(it, idx);
      const isLast = (idx === grouped.length - 1);
      const needLines = isLast ? (itemLines.length + 1) : itemLines.length;

      if (currentLineCount + needLines > MAX_PAGE_LINES && (currentLineCount + itemLines.length > MAX_PAGE_LINES || isLast)) {
        goToNextPageOrder();
      }

      itemLines.forEach(l => encoder.line(l));
      currentLineCount += itemLines.length;
    });

    if (currentLineCount + 1 > MAX_PAGE_LINES) {
      goToNextPageOrder();
    }
    encoder.line(doubleLine);
  } else if (type === 'return') {
    const {
      storeName = localStorage.getItem("tenantName") || "Toko",
      storeAddress = localStorage.getItem("tenantAddress") || "",
      storeTagline = "",
      storePhone = localStorage.getItem("tenantPhone") || "",
      storeFooterNote = "Barang yang diretur telah disesuaikan dengan stok.",
    } = data || {};

    const returnNumber = data?.return_number || "RTR-";
    const refTx = data?.transaction_number || data?.ref_transaction_number || "-";
    const cleanReturnNum = formatSimplifiedTxNum(returnNumber);
    const cleanRefTxNum = formatSimplifiedTxNum(refTx);

    const rawReturnDate = data?.return_date || data?.created_at || "";
    let formattedDate = "-";
    if (rawReturnDate) {
      try {
        const parsed = new Date(rawReturnDate);
        if (!isNaN(parsed.getTime())) {
          formattedDate = parsed.toLocaleDateString("id-ID", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          }) + " WIB";
        } else {
          formattedDate = String(rawReturnDate);
        }
      } catch {
        formattedDate = String(rawReturnDate);
      }
    }

    const { date: retDate, time: retTime } = splitDateTime(formattedDate);
    const fullDateTime = retDate !== "-"
      ? `${retDate}${retTime ? `, ${retTime}` : ""}`
      : (retTime || formattedDate || "-");

    const cashierName = getLoggedInUserName() || data?.created_by_name || "-";
    const customerName = data?.customer_name || "Pelanggan Umum";
    const reason = data?.reason || "Retur Barang";
    const refundMethod = data?.refund_method === "CASH" ? "Tunai" : (data?.refund_method || "Tunai");
    const totalRefund = Number(data?.total_refund_amount || data?.grand_total || data?.subtotal || 0);
    const items = data?.items || [];

    let currentLineCount = 0;

    const goToNextPage = () => {
      encoder.formFeed();
      encoder.newline(TOP_GAP_LINES);
      encoder.line(dashLine);
      encoder.line("No  Nama Produk                           Qty     Harga Satuan     Harga Total  ");
      encoder.line(dashLine);
      currentLineCount = TOP_GAP_LINES + 3;
    };

    // 1. Header: Store info (left 40) + Customer / Return info (right 40)
    const headerLeft = [
      String(storeName || '').trim(),
      String(storeAddress || '').trim(),
      storePhone ? String(storePhone).replace(/^handphone:\s*/i, "Telp: ") : '',
      storeTagline ? String(storeTagline).trim() : '',
      cashierName ? `Kasir: ${cashierName}` : '',
    ].filter(Boolean);

    const headerRight = [
      formatMetaRight("Nama Pelanggan", customerName),
      data?.unit_name || data?.asset_unit_name ? formatMetaRight("Unit Kendaraan", data.unit_name || data.asset_unit_name) : '',
      formatMetaRight("No. Retur", cleanReturnNum),
      refTx && refTx !== "-" ? formatMetaRight("Ref. Nota", cleanRefTxNum) : '',
      formatMetaRight("Tanggal & Waktu", fullDateTime),
      reason ? formatMetaRight("Alasan Retur", String(reason).substring(0, 22)) : '',
    ].filter(Boolean);

    const headerRows = Math.max(headerLeft.length, headerRight.length);
    encoder.bold(true);
    for (let i = 0; i < headerRows; i++) {
      encoder.line(pad2Col(headerLeft[i] || '', headerRight[i] || ''));
    }
    currentLineCount += headerRows;

    // 2. Table Header
    encoder.line(doubleLine);
    encoder.line("No  Nama Produk                           Qty     Harga Satuan     Harga Total  ");
    encoder.line(dashLine);
    currentLineCount += 3;

    // Persiapan fungsi baris item produk retur
    const getItemLines = (it, idx) => {
      const lines = [];
      const noStr = String(idx + 1).padStart(2, ' ') + '  '; // 4 chars
      let name = it.name || it.item_name || it.product_name || '-';
      if (it.variant_name) name += ` (${it.variant_name})`;
      const rawUnit = it?.unit_name || it?.unit || (it?.unit && typeof it.unit === 'object' ? it.unit.name : '') || it?.satuan || (!isKuliner ? 'Pcs' : '');
      const unit = typeof rawUnit === 'object' ? (rawUnit?.name || '') : String(rawUnit || '').trim();
      const qty = Number(it.qty || it.quantity || 1);
      const qtyStr = (String(qty) + (unit ? ` ${unit}` : '')).padStart(7, ' ') + ' '; // 8 chars
      const unitPrice = Number(it.price || it.unit_price || 0);
      const priceStr = (`Rp ${formatNumber(unitPrice)}`).padStart(15, ' ') + ' '; // 16 chars
      const totalIt = Number(it.total ?? it.total_price ?? (qty * unitPrice));
      const totalStr = (`Rp ${formatNumber(totalIt)}`).padStart(15, ' ') + ' '; // 16 chars

      if (name.length <= 34) {
        lines.push(noStr + name.padEnd(36, ' ') + qtyStr + priceStr + totalStr);
      } else {
        const w1 = name.substring(0, 34);
        const w2 = name.substring(34, 68);
        lines.push(noStr + w1.padEnd(36, ' ') + qtyStr + priceStr + totalStr);
        lines.push('    ' + w2.padEnd(36, ' ') + ' '.repeat(40));
      }

      const itemNotes = it.notes || it.note || it.reason || '';
      if (itemNotes) {
        lines.push('    * ' + String(itemNotes).substring(0, 74));
      }

      return lines;
    };

    // 3. Summary & Footer (2 Columns: 40 left, 40 right)
    const formatSummaryRow = (lbl, valStr) => {
      const l = String(lbl || '').padEnd(20, ' ').substring(0, 20);
      const v = String(valStr || '').padStart(18, ' ').substring(0, 18);
      return `${l}: ${v}`;
    };

    const rightSummary = [
      formatSummaryRow("Total Pengembalian", `Rp ${formatNumber(totalRefund)}`),
      formatSummaryRow("Metode Pengembalian", refundMethod),
      formatSummaryRow("Status", "DIRETUR"),
    ].filter(Boolean);

    const leftFooter = [
      "Hormat Kami,",
      "",
      cashierName ? `( ${cashierName} )` : "( ____________________ )",
      String(storeFooterNote || 'Barang yang diretur telah disesuaikan dengan stok.').substring(0, 38),
      "Powered by ASAlink",
    ].filter(Boolean);

    const footerRows = Math.max(leftFooter.length, rightSummary.length);
    const getFooterLines = () => {
      const fLines = [];
      fLines.push(doubleLine);
      for (let i = 0; i < footerRows; i++) {
        const l = (leftFooter[i] || '').padEnd(40, ' ').substring(0, 40);
        const r = (rightSummary[i] || '').padEnd(40, ' ').substring(0, 40);
        fLines.push(l + r);
      }
      return fLines;
    };

    // 4. Render Items dengan Paginasi Otomatis
    if (items && items.length > 0) {
      const footerLinesCount = getFooterLines().length;
      items.forEach((it, idx) => {
        const itemLines = getItemLines(it, idx);
        const isLast = (idx === items.length - 1);
        const needLines = isLast ? (itemLines.length + footerLinesCount) : itemLines.length;

        if (currentLineCount + needLines > MAX_PAGE_LINES && (currentLineCount + itemLines.length > MAX_PAGE_LINES || isLast)) {
          goToNextPage();
        }

        itemLines.forEach(l => encoder.line(l));
        currentLineCount += itemLines.length;
      });
    } else {
      encoder.line(pad2Col('Tidak ada item retur', '', 40, 40));
      currentLineCount += 1;
    }

    // 5. Render Footer & Summary
    const footerLines = getFooterLines();
    if (currentLineCount + footerLines.length > MAX_PAGE_LINES) {
      goToNextPage();
    }
    footerLines.forEach(l => encoder.line(l));
    currentLineCount += footerLines.length;
    encoder.bold(false);
  } else {
    encoder.bold(true);
    encoder.line(doubleLine);
    encoder.line(pad2Col(data?.storeName || 'Toko', new Date().toLocaleString("id-ID")));
    encoder.line(dashLine);
    encoder.bold(false);
  }

  const buffer = encoder.cut().encode();
  return buffer;
};

/**
 * Membangun buffer byte ESC/POS murni tanpa html2canvas.
 * Sangat presisi menghitung spasi untuk rata kiri-kanan.
 */
// Modifikasi fungsi ini untuk menerima opsi tambahan (customTitle)
const generateRawEscPosReceipt = async (data, type, forPreview = false, options = {}) => {
  const config = getReceiptConfig();
  if (config.orientation === 'horizontal' && (type === 'transaction' || type === 'order' || type === 'return')) {
    return generateRawHorizontalReceipt(data, type, forPreview, options);
  }
  const isKuliner = localStorage.getItem("PREVIEW_APPS") === "true";
  const cutFeedLines = Math.max(0, Number(options.cutFeedLines || 0));
  const { customTitle, printerWidth } = options;
  // Karena printer user terbukti 58mm, jadikan ini sebagai default mutlak 
  // apabila config belum tersimpan dengan benar di localStorage.
  const paperWidth = printerWidth || config.thermalWidth || '58mm';
  // Standard characters per line for receipt printers: 80mm = 48 chars, 58mm = 32 chars
  const cpl = paperWidth === '80mm' ? 48 : 32; 

  const padLR = (left, right) => {
    // Gunakan trimEnd agar spasi awal (indentasi addon/catatan) tidak terhapus
    const l = String(left || '').trimEnd();
    const r = String(right || '').trim();
    if (l.length + r.length >= cpl) return l + ' ' + r; 
    return l + ' '.repeat(cpl - l.length - r.length) + r;
  };
  
  // Fungsi memecah teks panjang menjadi array baris agar tidak memotong kata di tengah
  const wordWrap = (text) => {
    const str = String(text || '').trim();
    if (!str) return [];
    
    const lines = [];
    const words = str.split(/\s+/);
    let currentLine = '';
    
    for (const word of words) {
      if (currentLine.length + word.length + (currentLine ? 1 : 0) <= cpl) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        if (currentLine) lines.push(currentLine);
        
        // Jika satu kata sangat panjang (melebihi kapasitas 1 baris)
        if (word.length > cpl) {
          let w = word;
          while (w.length > cpl) {
            lines.push(w.substring(0, cpl));
            w = w.substring(cpl);
          }
          currentLine = w;
        } else {
          currentLine = word;
        }
      }
    }
    if (currentLine) lines.push(currentLine);
    return lines;
  };

  const wordWrapIndent = (text, indent, maxCpl = cpl) => {
    const str = String(text || '').trim();
    if (!str) return [];
    const effectiveWidth = Math.max(10, maxCpl - indent);
    const lines = [];
    const words = str.split(/\s+/);
    let currentLine = '';
    const prefix = ' '.repeat(indent);

    for (const word of words) {
      if (currentLine.length + word.length + (currentLine ? 1 : 0) <= effectiveWidth) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        if (currentLine) lines.push(prefix + currentLine);
        if (word.length > effectiveWidth) {
          let w = word;
          while (w.length > effectiveWidth) {
            lines.push(prefix + w.substring(0, effectiveWidth));
            w = w.substring(effectiveWidth);
          }
          currentLine = w;
        } else {
          currentLine = word;
        }
      }
    }
    if (currentLine) lines.push(prefix + currentLine);
    return lines;
  };

  const dashLine = '-'.repeat(cpl);

  // Helper untuk menambahkan spasi di awal string untuk MOCK BROWSER
  const centerTextMock = (text) => {
    const str = String(text || '').trim();
    if (str.length >= cpl) return str.substring(0, cpl);
    const spaces = Math.floor((cpl - str.length) / 2);
    return ' '.repeat(spaces > 0 ? spaces : 0) + str;
  };

  // Helper cerdas untuk bypass bug spasi di v3:
  // Mengirim spasi sebagai byte raw, lalu teks normalnya via enc.line()
  const printCenteredLine = (enc, text) => {
    const str = String(text || '').trim();
    if (forPreview) {
      enc.line(centerTextMock(str));
      return enc;
    }

    if (str.length >= cpl) {
      enc.line(str.substring(0, cpl));
      return enc;
    }

    const spaces = Math.floor((cpl - str.length) / 2);
    if (spaces > 0) {
      // Inject raw space bytes (0x20 = Spasi). 
      // Ini 100% diabaikan oleh fungsi word-wrap bawaan library!
      enc.raw(new Array(spaces).fill(0x20));
    }
    enc.line(str);
    return enc;
  };

  // Setelan 'columns' dan 'width' wajib dilempar ke library v3 agar
  // auto-wrap internalnya mengerti batas kertas kita yang sebenarnya!
  const encoder = forPreview ? new MockEncoder(cpl) : new EscPosEncoder({ columns: cpl, width: cpl });
  encoder.initialize();

  // Buka Laci Kasir via kabel RJ11 (Pin 2 dan Pin 5) pada nota transaksi kasir jika diaktifkan atau diminta eksplisit
  if (!forPreview && (options.openCashDrawer || (type === 'transaction' && config.autoOpenCashDrawer))) {
    encoder.raw([0x1B, 0x70, 0x00, 0x19, 0xFA, 0x1B, 0x70, 0x01, 0x19, 0xFA]);
  }

  // Proses logo jika ada. Samakan fallback dengan HTML receipt karena sebagian
  // receiptData tidak membawa storeLogoUrl dan hanya menyimpannya di localStorage.
  let logoCanvas = null;
  const rawLogoUrl = data?.storeLogoUrl || localStorage.getItem('tenantLogoUrl') || '';
  if (rawLogoUrl && config.printLogo !== false) {
    const base64 = rawLogoUrl.startsWith('data:image/')
      ? rawLogoUrl
      : await getBase64Image(rawLogoUrl);
    // esc-pos-encoder membaca pixel canvas. URL lintas domain yang tidak
    // mengizinkan CORS akan membuat canvas tainted dan menggagalkan seluruh
    // pengiriman raw USB. Dalam kondisi itu nota tetap dicetak tanpa logo.
    if (base64 && base64.startsWith('data:image/')) {
      logoCanvas = await loadLogoToCanvas(base64, paperWidth);
    }
  }
  
  if (logoCanvas && type !== 'order' && type !== 'shift') {
    // Disable ESC/POS logo printing temporarily as it causes garbled text on many generic mini printers
    // if (!forPreview) encoder.raw(new Uint8Array([0x1B, 0x61, 0x01]));
    encoder.image(logoCanvas, logoCanvas.width, logoCanvas.height, 'threshold');
    if (!forPreview) encoder.raw(new Uint8Array([0x1B, 0x61, 0x00]));
  }

  if (type === 'transaction') {
    const {
      storeName = "Toko",
      storeAddress = "",
      storeTagline = "",
      storePhone = "",
      storeFooterNote = "Terima Kasih Telah Menjadi Pelanggan Kami",
      meta = {},
      items = [],
      totals = {},
    } = data || {};

    const { date: trxDate, time: trxTime } = splitDateTime(meta.transactionDateTime);
    const transactionNumber = meta.transactionNumber || "-";
    const cleanTxNum = formatSimplifiedTxNum(transactionNumber);
    const fullDateTime = trxDate !== "-"
      ? `${trxDate}${trxTime ? `, ${trxTime}` : ""}`
      : (trxTime || "-");
    // Paksa gunakan nama user yang sedang login/aktif nyetak, abaikan nama kasir dari meta (misal dari created_by)
    const cashierName = getLoggedInUserName() || "-";
    const customerName = meta.customerName || "Walk-in";
    const transactionNotes = meta.transactionNotes || "";

    encoder.align('left'); // Pastikan selalu rata kiri
    
    // Header
    encoder.bold(true);
    printCenteredLine(encoder, storeName);
    encoder.bold(false);
    
    if (storeAddress) wordWrap(storeAddress).forEach(l => printCenteredLine(encoder, l));
    if (storeTagline) wordWrap(storeTagline).forEach(l => printCenteredLine(encoder, l));
    if (storePhone) printCenteredLine(encoder, storePhone.replace(/^handphone:\s*/i, ""));
    
    encoder.line(dashLine);

    // Meta Transaksi
    encoder.line(padLR(cleanTxNum, fullDateTime));
    encoder.line(padLR("Pelanggan:", customerName));
    const unitName = detectVehicleUnit(meta, items, data);
    if (unitName && String(unitName).trim()) {
      encoder.line(padLR("Unit:", String(unitName).trim()));
    }
    const sellerType = meta.sellerType || meta.seller_type || "";
    if (sellerType) {
      encoder.line(padLR("Tipe Penjualan:", sellerType));
    }
    const tableName = meta.tableName || meta.table_name || meta.table?.name || (typeof meta.table === "string" ? meta.table : "");
    if (tableName) {
      encoder.line(padLR("Meja:", tableName));
    }
    encoder.line(padLR("Kasir:", cashierName));
    encoder.line(dashLine);

    // Daftar Barang
    const groupedItems = groupItems(items);
    groupedItems.forEach(it => {
      let displayName = it.name || "-";
      if (it.variant_name) {
        displayName += ` (${it.variant_name})`;
      }
      
      const qty = Number(it.qty || it.quantity || 0);
      const price = Number(it.price || it.unit_price || 0);
      const calculatedItem = calculateOrderItemTotals(it);
      const totalIt = Number(it.total ?? it.total_price ?? calculatedItem.subtotal);
      const itemDiscount = Number(it.discount_amount || 0);
      const itemAdditionalCost = Number(it.additional_cost || 0);
      const baseTotal = calculatedItem.baseSubtotal;

      const itemNotes = !isKuliner ? (it.notes || it.note || "") : "";

      // Nama Item (Bisa panjang, kita wrap agar aman)
      wordWrap(displayName).forEach(l => encoder.bold(true).line(l).bold(false));
      if (itemNotes) {
        wordWrapIndent(`* ${itemNotes}`, 0, cpl).forEach(l => encoder.line(l));
      }
      // Qty x Harga di kiri, Total di kanan
      encoder.line(padLR(`${qty} X Rp ${formatNumber(price)}`, `Rp ${formatNumber(itemDiscount > 0 || itemAdditionalCost > 0 ? baseTotal : totalIt)}`));

      if (itemDiscount > 0) {
        formatReceiptMoneyRows('Diskon Item', itemDiscount, '-', cpl).forEach(line => encoder.line(line));
      }
      if (itemAdditionalCost > 0) {
        formatReceiptMoneyRows('Biaya Tambahan Item', itemAdditionalCost, '+', cpl).forEach(line => encoder.line(line));
      }
      if (itemDiscount > 0 || itemAdditionalCost > 0) {
        encoder.bold(true);
        formatReceiptMoneyRows('Total Item', totalIt, '', cpl).forEach(line => encoder.line(line));
        encoder.bold(false);
      }

      // Cetak addons di bawah item utama (menjorok 3 spasi, total sejajar rata kanan)
      if (it.addons && it.addons.length > 0) {
        it.addons.forEach(ad => {
          const adName = `+ ${ad.name}`;
          const adQty = Number(ad.qty || 0);
          const adPrice = Number(ad.price || 0);
          const adTotal = Number(ad.total || adQty * adPrice);
          const indentStr = "   "; // 3 spasi menjorok agar sejajar dengan + nama addon
          const adQtyStr = `${indentStr}${adQty} X Rp ${formatNumber(adPrice)}`;
          const adTotalStr = `Rp ${formatNumber(adTotal)}`;

          wordWrapIndent(adName, 3, cpl).forEach(l => encoder.line(l));
          encoder.line(padLR(adQtyStr, adTotalStr));
        });
      }
    });

    encoder.line(dashLine);

    // Ringkasan Harga
    const subtotal = Number(totals.subtotal ?? 0);
    const total = Number(totals.total ?? subtotal);
    const tunai = Number(totals.tunai ?? total);
    const change = Number(totals.change ?? 0);
    const isDP = !!totals.isDownPayment;
    const remaining = Number(totals.remaining ?? 0);
    const dpAmount = Number(totals.dpAmount ?? 0);
    const discount = Number(totals.discount ?? totals.discountTotal ?? 0);
    const additionalCosts = Number(totals.additionalCosts ?? totals.additional_costs ?? totals.additional ?? 0);
    const paymentMethodLabel = totals.paymentMethodLabel || totals.paymentMethod || "Tunai";

    const { discountLabel, additionalCostsLabel } = getReceiptAdjustmentLabels(data);

    const printSummaryRow = (label, valStr) => {
      if (label.length + valStr.length >= cpl) {
        encoder.line(label);
        encoder.line(padLR('', valStr));
      } else {
        encoder.line(padLR(label, valStr));
      }
    };

    const isDiscountFromType = !!(totals.discount_from_type || data?.totals?.discount_from_type);
    encoder.line(padLR('Sub Total', `Rp ${formatNumber(subtotal)}`));
    {/* [HIDDEN_SALE_TYPE_DISCOUNT_MARKUP]: Hide discount and markup rows if from sale type */}
    if (discount > 0 && !isDiscountFromType) printSummaryRow(discountLabel, `- Rp ${formatNumber(discount)}`);
    if (additionalCosts > 0 && !isDiscountFromType) printSummaryRow(additionalCostsLabel, `+ Rp ${formatNumber(additionalCosts)}`);
    
    encoder.bold(true).line(padLR('Total Tagihan', `Rp ${formatNumber(total)}`)).bold(false);
    
    const formatPaymentMethod = (m) => {
      const u = String(m || "").toUpperCase();
      if (u === "CASH") return "Tunai";
      if (u === "QRIS") return "QRIS";
      if (u === "TRANSFER") return "Transfer";
      if (u === "MIXED") return "Campuran";
      return m || "Tunai";
    };

    const pList = Array.isArray(totals.payments) && totals.payments.length > 0 ? totals.payments : [];
    if (pList.length > 1) {
      const sorted = [...pList].sort((a, b) => (a.payment_sequence || 0) - (b.payment_sequence || 0));
      sorted.forEach((p, idx) => {
        const pMethod = formatPaymentMethod(p.payment_method);
        const pAmount = Number(p.cash_amount || 0) + Number(p.electronic_amount || 0) || Number(p.amount || 0);
        let label = `Bayar (${pMethod})`;
        if (idx === 0) {
          label = `DP (${pMethod})`;
        } else if (idx === sorted.length - 1 && !isDP) {
          label = `Pelunasan (${pMethod})`;
        } else {
          label = `Bayar #${idx + 1} (${pMethod})`;
        }
        encoder.line(padLR(label, `Rp ${formatNumber(pAmount)}`));
      });
      if (change > 0 && !isDP) {
        encoder.line(padLR('Kembalian', `Rp ${formatNumber(change)}`));
      }
    } else if (pList.length === 1) {
      const p = pList[0];
      const pMethod = formatPaymentMethod(p.payment_method);
      const cAmt = Number(p.cash_amount || 0);
      const eAmt = Number(p.electronic_amount || 0);
      if (p.payment_method === 'MIXED' && cAmt > 0 && eAmt > 0) {
        encoder.line(padLR('Bayar (Tunai)', `Rp ${formatNumber(cAmt)}`));
        encoder.line(padLR('Bayar (Non-Tunai)', `Rp ${formatNumber(eAmt)}`));
        if (change > 0) encoder.line(padLR('Kembalian', `Rp ${formatNumber(change)}`));
      } else {
        const pAmount = cAmt || eAmt || Number(p.amount || 0) || tunai;
        encoder.line(padLR(`Bayar (${isDP ? `DP ${pMethod}` : pMethod})`, `Rp ${formatNumber(pAmount)}`));
        if (isDP) {
          encoder.bold(true).line(padLR('Sisa Tagihan', `Rp ${formatNumber(remaining || Math.max(total - pAmount, 0))}`)).bold(false);
        } else if (change > 0) {
          encoder.line(padLR('Kembalian', `Rp ${formatNumber(change)}`));
        }
      }
    } else {
      if (dpAmount > 0) encoder.line(padLR('DP', `Rp ${formatNumber(dpAmount)}`));
      encoder.line(padLR(`Bayar (${isDP ? 'DP' : paymentMethodLabel})`, `Rp ${formatNumber(tunai)}`));
      if (isDP) {
        encoder.bold(true).line(padLR('Sisa Tagihan', `Rp ${formatNumber(remaining || Math.max(total - tunai, 0))}`)).bold(false);
      } else if (change > 0) {
        encoder.line(padLR('Kembalian', `Rp ${formatNumber(change)}`));
      }
    }

    encoder.line(dashLine);

    if (transactionNotes) {
      encoder.line('Catatan:');
      wordWrap(transactionNotes).forEach(l => encoder.line(l));
    }

    // Footer
    if (storeFooterNote) {
       const notes = storeFooterNote.split('\n');
       notes.forEach(n => {
         wordWrap(n).forEach(l => printCenteredLine(encoder, l));
       });
    }
    encoder.newline();
    printCenteredLine(encoder, 'Powered by ASAlink');
  } 
  else if (type === 'shift') {
    const {
      storeName = "Toko",
      shift = {},
    } = data || {};
    
    const summary = shift.summary || {};
    const fmt = (n) => formatNumber(n);
    const formatDateOnly = (iso) => {
      if (!iso) return "-";
      try {
        return new Date(iso).toLocaleString("id-ID", {
          day: "2-digit", month: "short", year: "numeric",
        });
      } catch { return iso; }
    };
    const formatTimeOnly = (iso) => {
      if (!iso) return "-";
      try {
        return new Date(iso).toLocaleString("id-ID", {
          hour: "2-digit", minute: "2-digit"
        }) + " WIB";
      } catch { return iso; }
    };

    const revenueMethods = summary.revenue_by_payment_method || [];
    const totalPenjualan = revenueMethods.reduce((s, m) => s + Number(m.total || 0), 0);
    const pendingTxnCount = Number(summary.total_transaction_pending || 0);
    const cancelledTxnCount = Number(summary.total_transaction_cancelled || 0);
    const doneTxnCount = Number(summary.total_transaction_done || 0);
    const totalTxnCount = Number(summary.total_transaction_receipt || 0);

    const METHOD_LABEL = {
      CASH: "Tunai", QRIS: "QRIS", TRANSFER: "Transfer", MIXED: "Campuran",
    };



    const padLRBoldRight = (left, right) => {
      const l = String(left || '').trim();
      const r = String(right || '').trim();
      const spaceCount = Math.max(0, cpl - l.length - r.length);
      encoder.text(l)
             .text(' '.repeat(spaceCount))
             .bold(true)
             .text(r)
             .bold(false)
             .newline();
    };

    encoder.align('left');
    encoder.size(2, 2).bold(true);
    encoder.line(storeName);
    encoder.size(1, 1).bold(false);
    encoder.line('Nota Tutup Kasir | Settlement');
    encoder.line(dashLine);
    encoder.align('left');

    padLRBoldRight('Dibuka Oleh', shift.opened_by_name || shift.opened_by || "-");
    padLRBoldRight('Tanggal Buka', formatDateOnly(shift.opened_at));
    padLRBoldRight('Waktu Buka', formatTimeOnly(shift.opened_at));
    encoder.line(dashLine);

    padLRBoldRight('Modal Awal', `Rp ${fmt(shift.opening_cash || shift.start_cash)}`);
    padLRBoldRight('Total Penjualan', `Rp ${fmt(totalPenjualan)}`);
    
    revenueMethods.forEach(m => {
      const label = METHOD_LABEL[m.method] || m.method;
      const countLabel = m.count ? ` (${m.count})` : "";
      encoder.line(padLR(`. ${label}${countLabel}`, `Rp ${fmt(m.total)}`));
    });

    padLRBoldRight('Total Transaksi', `${fmt(totalTxnCount)} Nota`);
    encoder.line(padLR(`. Transaksi Pending`, `${fmt(pendingTxnCount)} Nota`));
    encoder.line(padLR(`. Transaksi Dibatalkan`, `${fmt(cancelledTxnCount)} Nota`));
    encoder.line(padLR(`. Transaksi Selesai`, `${fmt(doneTxnCount)} Nota`));

    padLRBoldRight('Total Pengeluaran', `Rp ${fmt(summary.total_cash_out || 0)}`);
    padLRBoldRight('Total Kas Kasir', `Rp ${fmt(summary.total_cashier_cash || 0)}`);
    encoder.line(dashLine);

    padLRBoldRight('Ditutup Oleh', shift.closed_by_name || "-");
    const dtClosed = shift.closed_at || new Date().toISOString();
    padLRBoldRight('Tanggal Tutup', formatDateOnly(dtClosed));
    padLRBoldRight('Waktu Tutup', formatTimeOnly(dtClosed));
    encoder.line(dashLine);

    padLRBoldRight('Jumlah Disetorkan', `Rp ${fmt(shift.closing_cash || 0)}`);
    const sisa = Math.max(0, (summary.total_cashier_cash || 0) - (shift.closing_cash || 0));
    padLRBoldRight('Sisa Uang Kas Kasir', `Rp ${fmt(sisa)}`);
    encoder.line(dashLine);

    const notesText = shift.notes ? shift.notes : "Tidak ada catatan pada saat tutup kasir.";
    const notes = notesText.split('\n');
    notes.forEach(n => {
       wordWrap(n).forEach(l => encoder.line(l));
    });

    encoder.newline();
    printCenteredLine(encoder, 'Powered by ASA Link');
    encoder.align('left');
  } 
  else if (type === 'customer_qr') {
    const {
      storeName = "Toko",
      storeAddress = "",
      storePhone = "",
      customerName = "-",
      customerPhone = "-",
      qrUrl = "",
      expiresAt = "",
      expiryMinutes = 10,
      createdAt = new Date().toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }),
    } = data || {};

    const finalQrUrl = normalizePublicUrl(qrUrl);

    encoder.align('left');
    const storeStr = String(storeName || '').trim();
    const halfCpl = Math.floor(cpl / 2);
    if (storeStr.length <= halfCpl) {
      const sp = Math.max(0, Math.floor((halfCpl - storeStr.length) / 2));
      encoder.size(2, 2).bold(true);
      if (sp > 0) encoder.raw(new Array(sp).fill(0x20));
      encoder.line(storeStr);
      encoder.size(1, 1).bold(false);
    } else {
      encoder.size(2, 2).bold(true);
      wordWrapIndent(storeStr, 0, halfCpl).forEach(l => {
        const sp = Math.max(0, Math.floor((halfCpl - l.trim().length) / 2));
        if (sp > 0) encoder.raw(new Array(sp).fill(0x20));
        encoder.line(l.trim());
      });
      encoder.size(1, 1).bold(false);
    }
    if (storeAddress) wordWrap(storeAddress).forEach(l => printCenteredLine(encoder, l));
    if (storePhone) printCenteredLine(encoder, storePhone.replace(/^handphone:\s*/i, ""));
    encoder.line(dashLine);
    encoder.bold(true);
    printCenteredLine(encoder, 'QR E-CATALOG PELANGGAN');
    encoder.bold(false);
    encoder.line(dashLine);

    encoder.line(padLR('Pelanggan:', customerName));
    encoder.line(padLR('Waktu Cetak:', createdAt));
    encoder.line(padLR('Masa Berlaku:', `${expiryMinutes} Menit`));
    encoder.line(dashLine);

    if (finalQrUrl) {
      encoder.align('center');
      try {
        encoder.qrcode(finalQrUrl, 2, paperWidth === '80mm' ? 6 : 5, 'm');
      } catch (qrErr) {
        console.warn('Encoder qrcode failed, printing URL text:', qrErr);
        encoder.line(finalQrUrl);
      }
      encoder.align('left');
      encoder.newline();
      if (expiresAt) {
        encoder.bold(true);
        printCenteredLine(encoder, `Berlaku s/d: ${expiresAt}`);
        encoder.bold(false);
      }
      encoder.line(dashLine);
      printCenteredLine(encoder, 'Scan QR di atas untuk memesan');
      printCenteredLine(encoder, 'via E-Catalog');
    }

    encoder.newline();
    printCenteredLine(encoder, 'Powered by ASAlink');
    encoder.align('left');
  }
  else if (type === 'return') {
    const {
      storeName = localStorage.getItem("tenantName") || "Toko",
      storeAddress = localStorage.getItem("tenantAddress") || "",
      storePhone = localStorage.getItem("tenantPhone") || "",
    } = data || {};

    const returnNumber = data?.return_number || "RTR-";
    const refTx = data?.transaction_number || data?.ref_transaction_number || "-";
    const customerName = data?.customer_name || "Pelanggan Umum";
    const returnDate = data?.return_date
      ? new Date(data.return_date).toLocaleDateString("id-ID", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }) + " WIB"
      : "-";
    const reason = data?.reason || "-";
    const items = data?.items || [];
    const totalRefund = Number(data?.total_refund_amount || 0);
    const refundMethod = data?.refund_method === "CASH" ? "Tunai" : (data?.refund_method || "Tunai");
    const cashierName = getLoggedInUserName() || data?.created_by_name || "-";

    encoder.align('left');
    const storeStr = String(storeName || '').trim();
    const halfCpl = Math.floor(cpl / 2);
    if (storeStr.length <= halfCpl) {
      const sp = Math.max(0, Math.floor((halfCpl - storeStr.length) / 2));
      encoder.size(2, 2).bold(true);
      if (sp > 0) encoder.raw(new Array(sp).fill(0x20));
      encoder.line(storeStr);
      encoder.size(1, 1).bold(false);
    } else {
      encoder.size(2, 2).bold(true);
      wordWrapIndent(storeStr, 0, halfCpl).forEach(l => {
        const sp = Math.max(0, Math.floor((halfCpl - l.trim().length) / 2));
        if (sp > 0) encoder.raw(new Array(sp).fill(0x20));
        encoder.line(l.trim());
      });
      encoder.size(1, 1).bold(false);
    }
    if (storeAddress) wordWrap(storeAddress).forEach(l => printCenteredLine(encoder, l));
    if (storePhone) printCenteredLine(encoder, storePhone.replace(/^handphone:\s*/i, ""));
    encoder.line(dashLine);
    encoder.bold(true);
    printCenteredLine(encoder, 'BUKTI RETUR PENJUALAN');
    encoder.bold(false);
    encoder.line(dashLine);

    encoder.line(padLR('No. Retur:', returnNumber));
    encoder.line(padLR('Ref. Nota:', refTx));
    encoder.line(padLR('Tanggal:', returnDate));
    encoder.line(padLR('Kasir:', cashierName));
    encoder.line(padLR('Pelanggan:', customerName));
    encoder.line(padLR('Metode Bayar:', refundMethod));
    if (reason && reason !== "-") {
      encoder.line(padLR('Alasan:', reason));
    }
    encoder.line(dashLine);

    encoder.bold(true);
    encoder.line('BARANG DIRETUR:');
    encoder.bold(false);

    // List of items
    items.forEach(itm => {
      const name = itm.item_name || itm.name || "Item";
      wordWrap(name).forEach(l => encoder.line(l));
      const qtyStr = `${itm.quantity || 1} ${itm.unit_name || 'PCS'} x Rp ${formatNumber(itm.unit_price || 0)}`;
      const totalStr = `Rp ${formatNumber(itm.total_price ?? ((itm.unit_price || 0) * (itm.quantity || 1)))}`;
      encoder.line(padLR(`  ${qtyStr}`, totalStr));
    });
    encoder.line(dashLine);

    // Total
    encoder.bold(true);
    encoder.line(padLR('TOTAL PENGEMBALIAN', `Rp ${formatNumber(totalRefund)}`));
    encoder.bold(false);
    encoder.line(dashLine);

    encoder.align('center');
    encoder.line('Barang yang diretur telah');
    encoder.line('disesuaikan dengan stok.');
    encoder.newline();
    printCenteredLine(encoder, 'Terima kasih');
    printCenteredLine(encoder, 'Powered by ASAlink');
    encoder.align('left');
  }
  else { // 'order'
    const {
      storeName = "Toko",
      meta = {},
      items = [],
    } = data || {};

    const transactionNumber = meta.transactionNumber || "-";
    const cleanTxNum = formatSimplifiedTxNum(transactionNumber);
    const formattedOrderTime = formatOrderDateTime(
      meta.transactionDateTime ||
        meta.orderDate ||
        meta.created_at ||
        meta.createdAt,
    );
    // Paksa gunakan nama user yang sedang login/aktif nyetak, abaikan nama kasir dari meta
    const cashierName = getLoggedInUserName() || "-";
    const sellerType = meta.sellerType || meta.seller_type || "Internal";
    const transactionNotes = meta.transactionNotes || "";
    
    const isAdditional = !!(meta.isAdditional || meta.isAdditionalOrder || meta.is_additional);
    encoder.align('left');
    printCenteredLine(encoder, customTitle || 'Nota Pesanan');
    encoder.line(dashLine);
    if (isAdditional) {
      encoder.line(padLR(`No. ${cleanTxNum}`, "Tambahan"));
    } else {
      encoder.line(`No. ${cleanTxNum}`);
    }
    encoder.line(padLR('Kasir:', cashierName || "-"));
    encoder.line(padLR('Pelanggan:', meta.customerName || "-"));
    encoder.line(padLR('Waktu:', formattedOrderTime || "-"));
    encoder.line(padLR('Tipe Penjualan:', sellerType));
    const tableName = meta.tableName || meta.table_name || meta.table?.name || (typeof meta.table === "string" ? meta.table : "");
    if (tableName) {
      encoder.bold(true).line(padLR('MEJA:', tableName)).bold(false);
    }
    encoder.line(dashLine);
    
    if (items && items.length > 0) {
      const groupedItems = groupItems(items);
      groupedItems.forEach((it, index) => {
        let displayName = it.name || "-";
        if (it.variant_name) {
          displayName += ` (${it.variant_name})`;
        }
        const qty = Number(it.qty || it.quantity || 0);
        const itemNotes = it.notes || it.note || "";
        const nameWrapped = wordWrap(displayName);
        encoder.bold(true).line(padLR(`${qty}x ${nameWrapped[0]}`, '')).bold(false);
        // Print remaining wrapped lines
        for(let i = 1; i < nameWrapped.length; i++){
           encoder.bold(true).line(`   ${nameWrapped[i]}`).bold(false);
        }
        if (itemNotes) {
           wordWrapIndent(`* ${itemNotes}`, 0, cpl).forEach(l => encoder.line(l));
        }
        
        // Print addons under the parent product
        if (it.addons && it.addons.length > 0) {
          it.addons.forEach(ad => {
            const adQty = Number(ad.qty || 0);
            wordWrapIndent(`+ ${ad.name} (x${adQty})`, 3, cpl).forEach(l => encoder.line(l));
          });
        }
        
        // Produk berikutnya langsung di baris berikutnya (1 spasi standar)
      });
    } else {
      printCenteredLine(encoder, 'Tidak ada item');
    }
    if (transactionNotes) {
      encoder.line(dashLine);
      encoder.line('Catatan:');
      wordWrap(transactionNotes).forEach(l => encoder.line(l));
    }
    encoder.newline();
  }

  // Printer dengan cutter membutuhkan feed agar footer melewati posisi pisau.
  const buffer = encoder.newline(cutFeedLines).cut().encode();
  return buffer;
};

// ============================================================================
// LABEL PESANAN PRINTER
// ============================================================================

/**
 * Generate ESC/POS bytes untuk satu label item.
 * Layout:
 *   [Nama Toko]          [Tipe Penjualan]
 *   [Nama Pelanggan]              [X/Total]
 *   [Qty] [Satuan]
 *   [Nama Produk] (bold/besar)
 *   [Varian] (jika ada)
 *   [Catatan] (jika ada)
 *   [+ Addon (xN)] (jika ada)
 *   [DD/MM/YY HH:MM]
 */
const generateRawEscPosLabel = async (labelData, paperWidth = '58mm', forPreview = false) => {
  const cpl = paperWidth === '80mm' ? 48 : 32;
  const {
    storeName = '', customerName = '', salesTypeName = '',
    productName = '', variantName = '', qty = 1, unit = '',
    notes = '', addons = [], datetime = '', index = 1, total = 1,
  } = labelData;

  const padLR = (left, right) => {
    const l = String(left || '').trimEnd();
    const r = String(right || '').trim();
    if (l.length + r.length >= cpl) return l.substring(0, cpl - r.length - 1) + ' ' + r;
    return l + ' '.repeat(cpl - l.length - r.length) + r;
  };

  const wordWrap = (text, maxLen = cpl) => {
    const str = String(text || '').trim();
    if (!str) return [];
    const words = str.split(/\s+/);
    const lines = [];
    let cur = '';
    for (const w of words) {
      if (cur.length + w.length + (cur ? 1 : 0) <= maxLen) {
        cur += (cur ? ' ' : '') + w;
      } else {
        if (cur) lines.push(cur);
        cur = w.length > maxLen ? w.substring(0, maxLen) : w;
      }
    }
    if (cur) lines.push(cur);
    return lines;
  };

  const counter = `${index}/${total}`;
  const qtyUnit = [qty, unit].filter(Boolean).join(' ');

  const encoder = forPreview ? new MockEncoder(cpl) : new EscPosEncoder({ columns: cpl, width: cpl });
  encoder.initialize();
  encoder.align('left');

  // Top Feed
  encoder.newline();

  // Row 1: Nama Toko (kiri) | Tipe Penjualan (kanan)
  encoder.line(padLR(storeName, salesTypeName));

  // Row 2: Nama Pelanggan (kiri)
  encoder.bold(true);
  wordWrap(customerName).forEach(l => encoder.line(l));
  encoder.bold(false);

  // Row 3: Qty + Satuan (Porsi)
  if (qtyUnit) encoder.line(qtyUnit);

  // Row 4-N: Nama Produk + Varian dalam kurung (bold, bisa multi-line)
  const fullProductName = variantName ? `${productName} (${variantName})` : productName;
  encoder.bold(true);
  wordWrap(fullProductName).forEach(l => encoder.line(l));
  encoder.bold(false);

  const cleanAddonName = (rawName, pName) => {
    let name = String(rawName || '').trim();
    const parentName = String(pName || '').trim();
    if (parentName && name.toLowerCase().startsWith(parentName.toLowerCase() + ' - ')) {
      return name.substring(parentName.length + 3);
    }
    if (name.includes(' - ')) {
      const parts = name.split(' - ');
      return parts[parts.length - 1];
    }
    return name;
  };

  // Addons (ditampilkan sebelum catatan)
  (addons || []).filter(a => a && a.name).forEach(a => {
    const cleanedName = cleanAddonName(a.name, productName);
    const adLine = `+ ${cleanedName}${a.qty > 1 ? ` (x${a.qty})` : ''}`;
    wordWrap(adLine).forEach(l => encoder.line(l));
  });

  // Catatan item (bawah sendiri setelah addons, diberi penanda bintang
  // karena printer thermal tidak mendukung cetak miring/italic)
  const cleanNote = notes ? String(notes).replace(/^catatan:\s*/i, '').trim() : '';
  if (cleanNote) {
    wordWrap(`* ${cleanNote}`).forEach(l => encoder.line(l));
  }

  // Spasi sedang sebelum tanggal & waktu
  encoder.newline();

  // Datetime (kiri) | Counter X/Total (kanan)
  encoder.line(padLR(datetime, counter));

  // Potong kertas antar label
  const buffer = encoder.newline().newline().cut().encode();
  return buffer;
};

/**
 * Cetak array label pesanan (1 label per item).
 * Tiap item dalam labelDataArray = 1 label.
 * Jika qty produk = 3, caller harus sudah expand array (3x entry).
 *
 * @param {Array} labelDataArray - array labelData object
 */
export const executePrintLabel = async (labelDataArray) => {
  if (!labelDataArray || labelDataArray.length === 0) return { method: 'none', success: false };

  const isKuliner = localStorage.getItem("PREVIEW_APPS") === "true";
  const config = getReceiptConfig();
  const paperWidth = config.labelPrinterWidth || '58mm';
  const labelMac = config.labelPrinterMac || '';
  const labelWindowsName = config.labelPrinterWindowsName || config.labelPrinterName || '';

  // 1. Bluetooth Android (Prioritas di aplikasi Android)
  if (window.bluetoothSerial) {
    const savedBt = localStorage.getItem('savedBluetoothPrinter')
      ? JSON.parse(localStorage.getItem('savedBluetoothPrinter'))
      : null;
    const targetMac = labelMac || savedBt?.address;

    if (targetMac) {
      try {
        for (let i = 0; i < labelDataArray.length; i++) {
          const buffer = await generateRawEscPosLabel(labelDataArray[i], paperWidth, false);
          await sendBluetoothRawBytes(targetMac, buffer);
          if (i < labelDataArray.length - 1) {
            await new Promise(r => setTimeout(r, 800));
          }
        }
        return { method: 'bluetooth', success: true };
      } catch (e) {
        console.error("Bluetooth print label error:", e);
        if (isKuliner) {
          throw new Error(e?.message || "Printer label belum terhubung. Silakan periksa Pengaturan Printer.");
        }
        return new Promise((resolve, reject) => {
          doWebLabelFallback(labelDataArray, paperWidth, resolve, reject);
        });
      }
    } else {
      if (isKuliner) {
        throw new Error("Printer label belum terhubung. Silakan sambungkan printer di Pengaturan Printer.");
      }
      return new Promise((resolve, reject) => {
        doWebLabelFallback(labelDataArray, paperWidth, resolve, reject);
      });
    }
  }

  // 2. Direct USB OTG (WebUSB)
  const savedUsb = getSavedUsbPrinter();
  if (savedUsb) {
    try {
      for (let i = 0; i < labelDataArray.length; i++) {
        const buffer = await generateRawEscPosLabel(labelDataArray[i], paperWidth, false);
        await sendRawUsbBytes(buffer);
        if (i < labelDataArray.length - 1) {
          await new Promise(r => setTimeout(r, 600));
        }
      }
      return { method: 'usb', success: true };
    } catch (usbErr) {
      console.error("Koneksi USB gagal untuk label:", usbErr);
      if (isKuliner) {
        throw new Error(usbErr?.message || "Printer label belum terhubung. Silakan periksa Pengaturan Printer.");
      }
      return new Promise((resolve, reject) => {
        doWebLabelFallback(labelDataArray, paperWidth, resolve, reject);
      });
    }
  }

  // 3. COM Bluetooth atau Virtual Serial COM Windows
  const savedSerial = getSavedSerialPrinter();
  if (savedSerial) {
    try {
      for (let i = 0; i < labelDataArray.length; i++) {
        const buffer = await generateRawEscPosLabel(labelDataArray[i], paperWidth, false);
        await sendRawSerialBytes(buffer);
        if (i < labelDataArray.length - 1) {
          await new Promise(r => setTimeout(r, 600));
        }
      }
      return { method: 'serial', success: true };
    } catch (serialErr) {
      console.error("Koneksi serial gagal untuk label:", serialErr);
      if (isKuliner) {
        throw new Error(serialErr?.message || "Printer label belum terhubung. Silakan periksa Pengaturan Printer.");
      }
      return new Promise((resolve, reject) => {
        doWebLabelFallback(labelDataArray, paperWidth, resolve, reject);
      });
    }
  }

  // 4. Printer Windows Spooler Helper (Bridge)
  const savedWindowsPrinter = getSavedWindowsPrinter();
  const targetLabelWindows = labelWindowsName || savedWindowsPrinter?.name;
  if (targetLabelWindows) {
    try {
      for (let i = 0; i < labelDataArray.length; i++) {
        const buffer = await generateRawEscPosLabel(labelDataArray[i], paperWidth, false);
        await sendRawWindowsBytes(buffer, targetLabelWindows);
        if (i < labelDataArray.length - 1) {
          await new Promise(r => setTimeout(r, 600));
        }
      }
      return { method: 'windows-spooler', success: true };
    } catch (bridgeErr) {
      console.error("Printer bridge gagal untuk label:", bridgeErr);
      if (isKuliner) {
        throw new Error(bridgeErr?.message || "Printer label belum terhubung. Silakan periksa Pengaturan Printer.");
      }
      return new Promise((resolve, reject) => {
        doWebLabelFallback(labelDataArray, paperWidth, resolve, reject);
      });
    }
  }

  // Jika tidak ada hardware printer terhubung
  if (isKuliner) {
    throw new Error("Printer label belum terhubung. Silakan sambungkan printer di Pengaturan Printer.");
  }

  // Web fallback (Khusus mode retail / non-kuliner)
  return new Promise((resolve, reject) => {
    doWebLabelFallback(labelDataArray, paperWidth, resolve, reject);
  });
};

const doWebLabelFallback = async (labelDataArray, paperWidth, resolve, reject) => {
  try {
    const html = buildLabelHtml(labelDataArray, paperWidth);
    let receiptWindow = null;
    try {
      receiptWindow = window.open("", "_blank");
    } catch (e) {
      receiptWindow = null;
    }

    if (receiptWindow) {
      receiptWindow.document.open();
      receiptWindow.document.write(html);
      receiptWindow.document.close();
      if (resolve) resolve({ method: 'web', success: true });
      return;
    }

    let iframe = document.getElementById("asalink-print-iframe");
    if (!iframe) {
      iframe = document.createElement("iframe");
      iframe.id = "asalink-print-iframe";
      iframe.style.position = "fixed";
      iframe.style.left = "-10000px";
      iframe.style.top = "-10000px";
      iframe.style.border = "0";
      iframe.style.visibility = "hidden";
      document.body.appendChild(iframe);
    }
    iframe.style.width = paperWidth || "58mm";
    iframe.style.height = "100mm";

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(html);
    iframeDoc.close();

    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        if (resolve) resolve({ method: 'web', success: true });
      } catch (err) {
        console.error("Iframe label print failed:", err);
        if (reject) reject(err);
      }
    }, 300);
  } catch (err) {
    if (reject) reject(err);
  }
};

/**
 * Helper to print Customer QR Code slip via configured printer
 * @param {Object} customerQRData
 */
export const executePrintCustomerQR = async (customerQRData) => {
  return executePrint(customerQRData, 'customer_qr');
};
